// Pure rekenfuncties voor de verlofmodule (verlofmodule-spec §7.3, §9, §10).
//
// Géén berekeningen in routes of componenten — alle saldo-berekeningen, opbouw-
// formules, FIFO-afboek-logica, bezettingscontrole en TVT-verval-logica komen
// hier samen. De API roept deze functies aan met geheugen-objecten (loose
// coupling met Prisma).
//
// Conventies:
//   - Alle invoer-types zijn lokale interfaces (geen import uit @streep/db) zodat
//     deze functies puur en standalone testbaar zijn.
//   - Datums zijn Date-objecten; serialisatie naar/uit ISO-strings doet de API.
//   - Alle uren zijn `number` (Decimal in DB); 2-decimalen-precisie (PO-besluit
//     2026-06-01; de kwartier-afronding is vervallen, zie regel 1 en `round2`).
//   - Regelnummers in commentaar verwijzen naar verlofmodule-specificatie §9.

// batch-70 vakantiecampagne (FO-525) - de jaargrens van het jaaroverzicht in NL-tijd.
import { amsterdamAllDayStartToUtc } from './amsterdam-tijd'

// ────────────────────────────────────────────────────────────────────────────
// Types — geheugen-objecten die de API mapt vanuit Prisma-records
// ────────────────────────────────────────────────────────────────────────────

// Batch-6: ouderschap (per kind) + senioren (per peildatum) zijn eigen emmers.
// Ze doen bewust NIET mee in de autoAfboekEmmers-default — opname alleen via
// een expliciete aanvraag van dat type. TVT doet sinds batch-69 (FO-522) wél
// mee: zie `STANDAARD_AUTO_AFBOEK_EMMERS`.
export type SaldoEmmerType =
  | 'WETTELIJK'
  | 'BOVENWETTELIJK'
  | 'ATV'
  | 'TVT_WERK'
  | 'TVT_REIS'
  | 'OUDERSCHAP_BETAALD'
  | 'OUDERSCHAP_ONBETAALD'
  | 'SENIORENVERLOF'

export interface SaldoEmmerInfo {
  id: string
  type: SaldoEmmerType
  jaar: number
  toegekend: number // totaalUren toegekend
  opgenomen: number // afgeboekt
  vervalDatum: Date | null
  toekenningsDatum: Date
}

export interface SaldoMutatieInfo {
  id: string
  emmerId: string | null
  richting: 'OPBOUW' | 'AFNAME' | 'VERVAL' | 'UITBETALING' | 'OVERDRACHT' | 'CORRECTIE'
  uren: number // signed: + bij OPBOUW/UITBETALING/OVERDRACHT, - bij AFNAME/VERVAL
  datum: Date
  vervalDatum: Date | null
}

export interface PublicHolidayInfo {
  date: Date
  isVerplicht: boolean // false voor Goede Vrijdag (regel 14)
  isLustrumOnly: boolean // true voor Bevrijdingsdag (regel 15)
}

export interface VerlofBeleidInfo {
  wettelijkFactor: number // default 4
  wettelijkVervalMaand: number // default 7 (juli volgend jaar)
  bovenwettelijkFactor: number // default 1
  bovenwettelijkVervalJaren: number // default 5
  atvUrenPerWeek: number // default 2
  atvUrenPerJaar: number // default 104
  atvAlleenBij38u40u: boolean // default true
  tvtMaxOverdrachtWeken: number // default 1 werkweek
  minimaleAanvraagTermijnWeken: number // default 4
  maxAaneengeslotenWerkdagen: number // default 15
  bezettingsdrempel: number // default 0.6
  // Procedure-correctie Deel 1/3: set emmers die meedoen aan de automatische
  // afboeking + de 15%-overschrijdingsmarge over het totale jaarrecht (default
  // 0.15). batch-69 (FO-522): de lijst is geordend en de volgorde-modus is
  // expliciet; defaults uit `STANDAARD_AUTO_AFBOEK_EMMERS`/`STANDAARD_AFBOEK_VOLGORDE`.
  autoAfboekEmmers: SaldoEmmerType[] // default TVT_WERK, TVT_REIS, ATV, WETTELIJK, BOVENWETTELIJK
  autoAfboekVolgorde: AfboekVolgordeModus // default VASTE_LIJST
  overschrijdingsMargePct: number // default 0.15
  // #59 — medisch verlof: betaald deel per bezoek (regulier/specialist) + jaar-cap.
  medischRegulierUren: number // default 2
  medischSpecialistUren: number // default 4
  medischMaxUrenPerJaar: number // default 8
  // batch-57 (FO-418) — automatische goedkeuring van aanvragen die UITSLUITEND
  // roostervrije (0-uurs) dagen bevatten. Default false = gewone TL-/HR-keten.
  autoGoedkeuringNulUren: boolean // default false
}

export interface VerlofPeriode {
  start: Date
  eind: Date | null
}

export interface AfboekAanvraag {
  hours: number
  methode: 'VERLOF' | 'ATV' | 'TVT' | 'BIJZONDER'
  tvtType?: 'TVT_WERK' | 'TVT_REIS' // alleen bij methode=TVT
}

export interface AfboekMutatie {
  emmerId: string
  emmerType: SaldoEmmerType
  uren: number // positief
}

export interface PlanningCelInfo {
  employeeId: string
  date: Date
  beschikbaar: boolean // false als al ingepland (werkbon, ander verlof, ziek)
}

export interface BezettingsConflict {
  date: Date
  bezettingsgraad: number // 0-1
  drempel: number // 0-1
  beschikbaar: number
  totaal: number
}

export interface TermijnFlags {
  isKorteTermijn: boolean // < 4 weken vooraf
  isLangeAaneengesloten: boolean // > 15 werkdagen
  weekenTotStart: number
  werkdagenInPeriode: number
}

export interface SaldoStand {
  toegekend: number
  opgebouwd: number
  opgenomen: number
  gereserveerd: number
  beschikbaar: number
}

export interface TVTVervalUitkomst {
  vervallenUren: number
  overgedragenUren: number
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers — datum-aware (regels 4, 14, 15, 65)
// ────────────────────────────────────────────────────────────────────────────

function isLustrumJaar(year: number): boolean {
  // Bevrijdingsdag is vrij in lustrumjaren (2025, 2030, 2035, ...) — regel 15.
  return year % 5 === 0
}

function sameDay(a: Date, b: Date, useUtc = false): boolean {
  return useUtc
    ? a.getUTCFullYear() === b.getUTCFullYear() &&
        a.getUTCMonth() === b.getUTCMonth() &&
        a.getUTCDate() === b.getUTCDate()
    : a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
}

/**
 * Bepaalt of een datum een echt-vrije feestdag is voor verlof-doeleinden.
 *
 * Regel 14: Goede Vrijdag is GEEN feestdag.
 * Regel 15: Bevrijdingsdag (5 mei) alleen vrij in lustrumjaren.
 *
 * Een PublicHoliday-record met isVerplicht=false (Goede Vrijdag) of
 * isLustrumOnly=true buiten lustrumjaar telt NIET als vrije dag.
 */
export function isFeestdag(datum: Date, feestdagen: PublicHolidayInfo[], useUtc = false): boolean {
  const match = feestdagen.find((f) => sameDay(f.date, datum, useUtc))
  if (!match) return false
  if (!match.isVerplicht) return false // bv. Goede Vrijdag
  // `useUtc` (spec 23): de uren-split-engine rekent in UTC-dagen; vergelijk dan
  // óók het lustrum-jaar op de UTC-datum. Verlof gebruikt de lokale variant.
  const jaar = useUtc ? datum.getUTCFullYear() : datum.getFullYear()
  if (match.isLustrumOnly && !isLustrumJaar(jaar)) return false
  return true
}

/**
 * Telt werkdagen in een periode, exclusief weekenden en feestdagen.
 * Regel 4: weekenden + feestdagen worden automatisch uitgesloten.
 * Regel 65: verlof op een feestdag telt niet als verlofdag.
 */
export function calculateWerkdagen(
  start: Date,
  eind: Date,
  feestdagen: PublicHolidayInfo[],
): number {
  if (eind < start) return 0
  let count = 0
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const eindNorm = new Date(eind.getFullYear(), eind.getMonth(), eind.getDate())
  while (cursor <= eindNorm) {
    const dow = cursor.getDay()
    const isWeekend = dow === 0 || dow === 6
    if (!isWeekend && !isFeestdag(cursor, feestdagen)) {
      count++
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return count
}

// ────────────────────────────────────────────────────────────────────────────
// Opbouw-formules (regels 32, 33, 34, 40)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wettelijk verlof = 4× contracturen per week per jaar (CAO Metaal en Techniek).
 * Bij 38u contract: 4 × 38 = 152u (= 20 dagen van 7,6u, of bij ATV-opbouw 19 dagen van 8u).
 * Vervalt 1 juli volgend jaar — regel 32.
 */
export function berekenWettelijkOpbouw(
  contractHoursPerWeek: number,
  beleid?: Pick<VerlofBeleidInfo, 'wettelijkFactor'>,
): number {
  if (contractHoursPerWeek <= 0) return 0
  const factor = beleid?.wettelijkFactor ?? 4
  return contractHoursPerWeek * factor
}

/**
 * Bovenwettelijk verlof = 1× contracturen per week per jaar.
 * Bij 38u contract: 38u (= 5 dagen van 7,6u). Totaal wettelijk + bovenwettelijk =
 * 5× contracturen per week (= 25 dagen). Vervalt 5 jaar na opbouw — regel 33.
 */
export function berekenBovenwettelijkOpbouw(
  contractHoursPerWeek: number,
  beleid?: Pick<VerlofBeleidInfo, 'bovenwettelijkFactor'>,
): number {
  if (contractHoursPerWeek <= 0) return 0
  const factor = beleid?.bovenwettelijkFactor ?? 1
  return contractHoursPerWeek * factor
}

/**
 * ATV-opbouw = 2 uur per week × 52 weken = 104u per jaar, MAAR alleen voor
 * medewerkers die 38u werken en 40u maken (regel 40, spec §2.2).
 * Bij andere contract/werk-combinaties: 0u.
 */
export function berekenATVOpbouw(
  contractHoursPerWeek: number,
  werkUrenPerWeek: number,
  beleid?: Pick<VerlofBeleidInfo, 'atvUrenPerJaar' | 'atvAlleenBij38u40u'>,
): number {
  const alleenBij38u40u = beleid?.atvAlleenBij38u40u ?? true
  if (alleenBij38u40u) {
    if (contractHoursPerWeek !== 38 || werkUrenPerWeek !== 40) return 0
  }
  return beleid?.atvUrenPerJaar ?? 104
}

/**
 * Daguren-conversie voor verlof-aantallen — regel 40.
 *
 * Dagkost = werkelijke werkuren per week / werkdagen per week (exacte decimalen).
 * ATV zit in de TELLER (werkUrenPerWeek), niet in een aparte tak: bij 38u contract
 * met ATV is werkUrenPerWeek 40 (zie `getTotaalWerkbareUren`). Een formule dekt
 * 5- en 4-daagse contracten:
 *
 * - 38 werkuren / 5 werkdagen (geen ATV) → 7,6u/dag
 * - 40 werkuren / 5 werkdagen (38u→40u ATV) → 8u/dag
 * - 36 werkuren / 4 werkdagen (4-daags contract) → 9u/dag
 *
 * `werkUrenPerWeek` = `Employee.werkUrenPerWeek ?? contracturen (+ ATV)` —
 * de caller levert het werkelijke aantal werkuren. `werkdagenPerWeek` =
 * `Employee.werkdagenPerWeek` (default 5). Niet-positieve invoer → 0 (geen
 * deling door nul).
 */
export function berekenDaguren(werkUrenPerWeek: number, werkdagenPerWeek: number): number {
  if (werkUrenPerWeek <= 0 || werkdagenPerWeek <= 0) return 0
  return round2(werkUrenPerWeek / werkdagenPerWeek)
}

// ────────────────────────────────────────────────────────────────────────────
// Uren ⇄ dagen-weergave (portal-verlof eenheidstoggle)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Uren → dagen voor de portal-weergave. Eén lineaire deling door de
 * (per-medewerker) `daguren`, afgerond op 2 decimalen. Niet-positieve `daguren`
 * → 0 (geen deling door nul).
 *
 * Uren zijn leidend. Voor dag-aligned waarden (hele/halve dagen) is de dagweergave
 * exact. Voor niet-aligned restjes (dagdeel-boekingen op 0,25u) kan de per-waarde-
 * afronding tot 0,01 dag afwijken van de som: `round2(a/d) − round2(b/d)` is in het
 * algemeen niet gelijk aan `round2((a−b)/d)`. De dagweergave is dus een afgeronde
 * view; de uren blijven de exacte bron en worden nooit opgeslagen of teruggerekend.
 */
export function urenNaarDagen(uren: number, daguren: number): number {
  if (daguren <= 0) return 0
  return round2(uren / daguren)
}

/**
 * Decimale-dagen-weergave: NL-komma, max 2 decimalen, achterloze nullen weg.
 * Meervoud "dagen" bij gehele waarden ≠ 1; enkelvoud "dag" bij 1 én bij elke
 * decimale waarde (vgl. "anderhalve dag", "vierenhalve dag"). Voorbeelden:
 * `20 dagen`, `1 dag`, `1,5 dag`, `0,66 dag`.
 */
export function formatVerlofDagen(uren: number, daguren: number): string {
  const dagen = urenNaarDagen(uren, daguren)
  // Enkelvoud bij ±1 en bij elke decimale waarde ("anderhalve dag"); meervoud bij
  // gehele waarden ≠ 1 (0, 2, …). |dagen| zodat −1 ook "−1 dag" geeft.
  const enkelvoud = Math.abs(dagen) === 1 || !Number.isInteger(dagen)
  const getal = dagen.toLocaleString('nl-NL', { maximumFractionDigits: 2 })
  return `${getal} ${enkelvoud ? 'dag' : 'dagen'}`
}

/**
 * Uren-weergave voor verlof — byte-identiek aan de eerder per-bestand
 * gedupliceerde `fmtUren` ("7.6u", "8u", "152u"). Eén geteste bron, zodat de
 * uren-modus visueel ongewijzigd blijft t.o.v. vóór de eenheidstoggle.
 */
export function formatVerlofUren(uren: number): string {
  return `${uren.toFixed(2).replace(/\.00$/, '')}u`
}

// ────────────────────────────────────────────────────────────────────────────
// Pro-rata bij in-/uitdiensttreding (regels 37, 38)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Proportionele toekenning op basis van werkdagen-in-jaar vs werkdagen-in-periode.
 * Regel 37: pro-rata bij indiensttreding na 1 januari.
 * Regel 38: pro-rata bij uitdiensttreding vóór 31 december.
 *
 * Geretourneerd is afgerond op kwartier-precisie (regel 1) zodat de waarde
 * direct als saldo kan worden geboekt.
 */
export function proRataUren(
  jaarUren: number,
  year: number,
  periode: { start: Date; eind?: Date | null },
  feestdagen: PublicHolidayInfo[] = [],
): number {
  const yearStart = new Date(year, 0, 1)
  const yearEnd = new Date(year, 11, 31)
  const start = periode.start < yearStart ? yearStart : periode.start
  const eind = periode.eind && periode.eind < yearEnd ? periode.eind : yearEnd
  if (eind < start) return 0
  const werkdagenInJaar = calculateWerkdagen(yearStart, yearEnd, feestdagen)
  const werkdagenInPeriode = calculateWerkdagen(start, eind, feestdagen)
  if (werkdagenInJaar === 0) return 0
  const ratio = werkdagenInPeriode / werkdagenInJaar
  return roundToKwartier(jaarUren * ratio)
}

/**
 * @deprecated Verlof rondt sinds 2026-06-01 (PO-besluit) op exacte 2 decimalen
 * i.p.v. kwartier. Deze alias blijft voor bestaande imports en gedraagt zich
 * identiek aan `round2`. Nieuwe code: gebruik `round2`.
 */
export function roundToKwartier(uren: number): number {
  return round2(uren)
}

// ────────────────────────────────────────────────────────────────────────────
// Saldo-presentatie (regel 41 — actueel-vs-toegekend)
// ────────────────────────────────────────────────────────────────────────────

function getDayOfYear(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 1)
  const diff = d.getTime() - start.getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

function getIsoWeekNumber(d: Date): number {
  // ISO 8601 week — kopie van iso-week.ts logica om dependency-loop te vermijden.
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const dayNr = (target.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = target.valueOf()
  target.setMonth(0, 1)
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7))
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000)
}

/**
 * Berekent het ACTUEEL opgebouwde saldo op een peildatum, voor presentatie naast
 * het TOEGEKENDE jaarsaldo (regel 41).
 *
 * - ATV: weeknummer × atvUrenPerWeek (incrementeel, max = toegekend pro-rata jaarrecht)
 * - Wettelijk + Bovenwettelijk: pro-rata over WERKDAGEN als `feestdagen` is
 *   meegegeven (spec §2.3), anders backward-compatible pro-rata over kalenderdagen.
 * - TVT: niet door deze functie afgehandeld (zie berekenSaldoOpDatum) — TVT
 *   bouwt op via mutaties, niet via formule.
 */
export function berekenActueelOpgebouwd(
  emmer: SaldoEmmerInfo,
  peildatum: Date,
  beleid?: Pick<VerlofBeleidInfo, 'atvUrenPerWeek' | 'atvUrenPerJaar'>,
  feestdagen: PublicHolidayInfo[] = [],
): number {
  if (peildatum.getFullYear() !== emmer.jaar) {
    // Buiten het emmer-jaar: óf 100% (na jaareinde) óf 0% (vóór jaarbegin).
    if (peildatum.getFullYear() > emmer.jaar) return emmer.toegekend
    return 0
  }

  if (emmer.type === 'ATV') {
    const week = getIsoWeekNumber(peildatum)
    const perWeek = beleid?.atvUrenPerWeek ?? 2
    // Cap altijd op het daadwerkelijk TOEGEKENDE (pro-rata) recht, nooit op de
    // un-pro-rata CAO-jaarnorm: `toegekend = atvUrenPerJaar × proRataFactor ≤
    // atvUrenPerJaar`, dus een mid-year starter (toegekend < 104) mag nooit tot
    // 104 "actueel opgebouwd" tonen. Voorheen liet `beleid?.atvUrenPerJaar` dat
    // toe → het detail-endpoint (policy meegegeven) week boven het lijst-endpoint
    // (geen policy) uit. `beleid` bepaalt nu alleen nog het opbouwtempo (perWeek).
    return Math.min(week * perWeek, emmer.toegekend)
  }

  if (emmer.type === 'WETTELIJK' || emmer.type === 'BOVENWETTELIJK') {
    const jaar = peildatum.getFullYear()
    if (feestdagen.length > 0) {
      // Pro-rata over werkdagen (spec §2.3): sluit weekenden + feestdagen uit.
      const werkdagenTotPeildatum = calculateWerkdagen(new Date(jaar, 0, 1), peildatum, feestdagen)
      const werkdagenInJaar = calculateWerkdagen(
        new Date(jaar, 0, 1),
        new Date(jaar, 11, 31),
        feestdagen,
      )
      const ratio = werkdagenInJaar > 0 ? werkdagenTotPeildatum / werkdagenInJaar : 0
      return roundToKwartier(emmer.toegekend * ratio)
    }
    // Geen feestdagen meegegeven: backward-compatible pro-rata over kalenderdagen.
    const dagInJaar = getDayOfYear(peildatum)
    const dagenInJaar = isLeapYear(jaar) ? 366 : 365
    return roundToKwartier((emmer.toegekend / dagenInJaar) * dagInJaar)
  }

  // TVT: caller moet mutaties optellen via berekenSaldoOpDatum
  return 0
}

/**
 * Berekent saldo-stand op een specifieke datum, gegeven de toegekende emmer + alle
 * mutaties. Voor TVT-emmers is dit dé manier (geen formule maar mutatie-sum).
 *
 * `beschikbaar = toegekend + (som mutaties tot peildatum) − opgenomen − gereserveerd`
 *
 * Mutaties met `vervalDatum < peildatum` tellen niet mee als nog beschikbaar
 * (regels 32-34, 46).
 */
export function berekenSaldoOpDatum(
  emmer: SaldoEmmerInfo,
  mutaties: SaldoMutatieInfo[],
  peildatum: Date,
  gereserveerd = 0,
): SaldoStand {
  const relevant = mutaties.filter((m) => m.emmerId === emmer.id && m.datum <= peildatum)
  const opbouw = relevant
    .filter(
      (m) => m.richting === 'OPBOUW' || m.richting === 'OVERDRACHT' || m.richting === 'CORRECTIE',
    )
    .filter((m) => !m.vervalDatum || m.vervalDatum > peildatum)
    .reduce((s, m) => s + m.uren, 0)
  const opgenomen = relevant
    .filter((m) => m.richting === 'AFNAME' || m.richting === 'UITBETALING')
    .reduce((s, m) => s + Math.abs(m.uren), 0)
  const vervallen = relevant
    .filter((m) => m.richting === 'VERVAL')
    .reduce((s, m) => s + Math.abs(m.uren), 0)

  const toegekend = emmer.toegekend
  // B3 — read-time verval: na de eigen vervalDatum van de emmer is het resterende,
  // nog-niet-opgenomen, nog-niet-vervallen deel gelapst (regels 32-34), ook al heeft
  // de verval-cron de VERVAL-mutatie nog niet geschreven (die draait 06:45; tussen
  // middernacht en dan zou het verlof anders nog "beschikbaar" en boekbaar zijn).
  // STRIKT `>`: op de vervalDatum zelf rekent de verval-cron (peildatum = expiresAt)
  // nog met het echte restant zodat hij de VERVAL-mutatie kán schrijven. Idempotent
  // als die mutatie er al is: dan is het restant ~0 en verandert er niets.
  let vervallenEff = vervallen
  if (emmer.vervalDatum && peildatum > emmer.vervalDatum) {
    const restNaMutaties = opbouw + toegekend - vervallen - opgenomen
    if (restNaMutaties > 0) vervallenEff = vervallen + restNaMutaties
  }
  const opgebouwd = opbouw + toegekend - vervallenEff
  const beschikbaar = opgebouwd - opgenomen - gereserveerd

  return {
    toegekend,
    opgebouwd: roundToKwartier(opgebouwd),
    opgenomen: roundToKwartier(opgenomen),
    gereserveerd: roundToKwartier(gereserveerd),
    beschikbaar: roundToKwartier(beschikbaar),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Afboek-logica (regels 30, 31 — wettelijk-eerst + FIFO)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Bepaalt op welke emmer(s) een aanvraag wordt afgeboekt, in welke volgorde.
 *
 * Regel 30: bij methode=VERLOF → eerst WETTELIJK, dan BOVENWETTELIJK.
 * Regel 31: binnen elk potje FIFO — oudste opbouw (laagste toekenningsDatum) eerst.
 * Regel 35: beschikbaar saldo mag niet negatief — caller moet dat checken vóór commit.
 *           Deze functie boekt zoveel mogelijk en geeft de overgebleven uren terug
 *           via een mutatie zonder emmer-hit (overflow op laatste emmer).
 *
 * Geen afboeking bij methode=BIJZONDER (regel 58) of categorieën zonder vakantie-impact
 * (onbetaald/zwangerschap/geboorte — caller bepaalt dat).
 */
export function afboekOpEmmers(
  aanvraag: AfboekAanvraag,
  emmers: SaldoEmmerInfo[],
): AfboekMutatie[] {
  if (aanvraag.hours <= 0) return []
  if (aanvraag.methode === 'BIJZONDER') return [] // regel 58

  const sortedFor = (types: SaldoEmmerType[]): SaldoEmmerInfo[] => {
    return emmers
      .filter((e) => types.includes(e.type))
      .sort((a, b) => {
        // FIFO: oudste opbouw eerst (regel 31). Tiebreak op jaar.
        if (a.toekenningsDatum.getTime() !== b.toekenningsDatum.getTime()) {
          return a.toekenningsDatum.getTime() - b.toekenningsDatum.getTime()
        }
        return a.jaar - b.jaar
      })
  }

  let queue: SaldoEmmerInfo[]
  if (aanvraag.methode === 'VERLOF') {
    queue = [...sortedFor(['WETTELIJK']), ...sortedFor(['BOVENWETTELIJK'])] // regel 30
  } else if (aanvraag.methode === 'ATV') {
    queue = sortedFor(['ATV'])
  } else if (aanvraag.methode === 'TVT') {
    if (!aanvraag.tvtType) return []
    queue = sortedFor([aanvraag.tvtType])
  } else {
    return []
  }

  const mutaties: AfboekMutatie[] = []
  let resterend = aanvraag.hours
  for (const emmer of queue) {
    if (resterend <= 0) break
    const beschikbaar = Math.max(0, emmer.toegekend - emmer.opgenomen)
    const af = Math.min(beschikbaar, resterend)
    if (af > 0) {
      mutaties.push({ emmerId: emmer.id, emmerType: emmer.type, uren: roundToKwartier(af) })
      resterend = roundToKwartier(resterend - af)
    }
  }

  // Overflow als saldo niet voldoende — caller waarschuwt (regel 7); HR mag toch
  // committen (regel 35 toestaan via correctie). We bookkeepen de overflow
  // tegen de laatste emmer in de queue zodat de mutaties optelt tot aanvraag.hours.
  if (resterend > 0 && queue.length > 0) {
    const last = queue[queue.length - 1]
    const existing = mutaties.find((m) => m.emmerId === last.id)
    if (existing) {
      existing.uren = roundToKwartier(existing.uren + resterend)
    } else {
      mutaties.push({ emmerId: last.id, emmerType: last.type, uren: roundToKwartier(resterend) })
    }
  }

  return mutaties
}

// ────────────────────────────────────────────────────────────────────────────
// Bezetting (regels 60-64)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Identificeert dagen waarop bezetting onder de drempel komt voor een verlof-periode.
 * Regel 62: WAARSCHUWING (geen blokkade). Regel 64: ziek telt mee in bezetting.
 *
 * `planning` bevat per dag per medewerker een record met `beschikbaar=true`
 * als die persoon werkt of beschikbaar is. Conflict ontstaat als
 * (beschikbaar / totaal) < drempel, exclusief de aanvrager zelf.
 */
export function valideerBezetting(
  periode: VerlofPeriode,
  planning: PlanningCelInfo[],
  drempel = 0.6,
  excludeEmployeeId?: string,
): BezettingsConflict[] {
  if (!periode.eind) return []
  const conflicts: BezettingsConflict[] = []
  const cursor = new Date(
    periode.start.getFullYear(),
    periode.start.getMonth(),
    periode.start.getDate(),
  )
  const eindNorm = new Date(
    periode.eind.getFullYear(),
    periode.eind.getMonth(),
    periode.eind.getDate(),
  )
  while (cursor <= eindNorm) {
    const cellen = planning.filter(
      (p) => sameDay(p.date, cursor) && p.employeeId !== excludeEmployeeId,
    )
    if (cellen.length > 0) {
      const beschikbaar = cellen.filter((p) => p.beschikbaar).length
      const ratio = beschikbaar / cellen.length
      if (ratio < drempel) {
        conflicts.push({
          date: new Date(cursor),
          bezettingsgraad: ratio,
          drempel,
          beschikbaar,
          totaal: cellen.length,
        })
      }
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return conflicts
}

// ────────────────────────────────────────────────────────────────────────────
// Aanvraagtermijn-flags (regels 9, 10)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Berekent de termijn-flags die het aanvraagformulier toont:
 * - regel 9: < 4 weken vooraf = isKorteTermijn (waarschuwing + melding TL)
 * - regel 10: > 15 werkdagen aaneengesloten = isLangeAaneengesloten (waarschuwing)
 */
export function bepaalAanvraagtermijnFlags(
  periode: VerlofPeriode,
  vandaag: Date,
  feestdagen: PublicHolidayInfo[] = [],
  beleid?: Pick<VerlofBeleidInfo, 'minimaleAanvraagTermijnWeken' | 'maxAaneengeslotenWerkdagen'>,
): TermijnFlags {
  const minWeken = beleid?.minimaleAanvraagTermijnWeken ?? 4
  const maxDagen = beleid?.maxAaneengeslotenWerkdagen ?? 15
  const msPerWeek = 1000 * 60 * 60 * 24 * 7
  const weekenTotStart = (periode.start.getTime() - vandaag.getTime()) / msPerWeek
  const eindForCount = periode.eind ?? periode.start
  const werkdagenInPeriode = calculateWerkdagen(periode.start, eindForCount, feestdagen)
  return {
    isKorteTermijn: weekenTotStart < minWeken,
    isLangeAaneengesloten: werkdagenInPeriode > maxDagen,
    weekenTotStart: Math.round(weekenTotStart * 10) / 10,
    werkdagenInPeriode,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// TVT-verval (regels 46, 52)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Bepaalt op een peildatum (typisch 31-12) hoeveel TVT-uren vervallen en hoeveel
 * worden overgedragen naar volgend jaar. Regel 46: max 1 werkweek overdracht.
 * Mutaties met `vervalDatum <= peildatum` zijn kandidaten voor verval. Het
 * overdracht-budget wordt FIFO gevuld vanaf de meest recente mutaties.
 */
export function bepaalTVTVerval(
  mutaties: SaldoMutatieInfo[],
  peildatum: Date,
  maxOverdrachtUren: number,
): TVTVervalUitkomst {
  const kandidaten = mutaties.filter(
    (m) => m.richting === 'OPBOUW' && m.vervalDatum && m.vervalDatum <= peildatum && m.uren > 0,
  )
  // Sorteer op vervalDatum DESC — meest recent eerst om naar overdracht te shiften.
  const sorted = [...kandidaten].sort(
    (a, b) => (b.vervalDatum?.getTime() ?? 0) - (a.vervalDatum?.getTime() ?? 0),
  )
  let overdrachtBudget = roundToKwartier(maxOverdrachtUren)
  let overgedragen = 0
  let vervallen = 0
  for (const m of sorted) {
    if (overdrachtBudget > 0) {
      const overdracht = Math.min(m.uren, overdrachtBudget)
      overgedragen += overdracht
      overdrachtBudget -= overdracht
      const restant = m.uren - overdracht
      if (restant > 0) vervallen += restant
    } else {
      vervallen += m.uren
    }
  }
  return {
    vervallenUren: roundToKwartier(vervallen),
    overgedragenUren: roundToKwartier(overgedragen),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Verwacht saldo — vooruit-projectie op peildatum (vakantie-campagne)
// ────────────────────────────────────────────────────────────────────────────

export type VerwachtSaldoWaarschuwingType =
  | 'ONVOLDOENDE_SALDO'
  | 'LAAG_RESTEREND'
  | 'GEBASEERD_OP_VERWACHTE_OPBOUW'
  | 'CONTRACT_EINDIGT_VOOR_VAKANTIE'
  | 'VERVAL_VOOR_PEILDATUM'

export interface VerwachtSaldoWaarschuwing {
  type: VerwachtSaldoWaarschuwingType
  details: Record<string, unknown>
}

export interface EmmerVerwachting {
  type: SaldoEmmerType
  huidig: number
  toekomstigeOpbouw: number
  toekomstigeToekenning: number
  verval: number
  verwacht: number
}

export interface VerwachtSaldoResult {
  huidigBeschikbaar: number
  verwachtOpPeildatum: number
  gereserveerd: number
  vrijeRuimte: number
  perEmmer: EmmerVerwachting[]
  waarschuwingen: VerwachtSaldoWaarschuwing[]
}

export interface VerwachtSaldoInput {
  emmers: SaldoEmmerInfo[]
  mutaties: SaldoMutatieInfo[]
  contractHoursPerWeek: number
  atvEnabled: boolean
  atvHoursPerWeek: number
  contractEndDate?: Date | null
  startDate?: Date | null
  beleid: VerlofBeleidInfo
  gereserveerdUren: number
  peildatum: Date
  vandaag: Date
  minRestUren?: number
  tvtMaxOverdrachtUren?: number
}

/**
 * Projecteert het verlofsaldo vooruit naar een peildatum (typisch vakantie-startdatum).
 *
 * Per emmer wordt berekend:
 *   verwacht = huidig + toekomstigeOpbouw + toekomstigeToekenning - verval
 *
 * - `toekomstigeOpbouw` = nog te verwachten opbouw binnen het lopende jaar (pro-rata
 *   tot peildatum óf jaareinde wanneer peildatum in volgend jaar valt).
 * - `toekomstigeToekenning` = simulatie van nieuwe jaartoekenningen (WETTELIJK,
 *   BOVENWETTELIJK, ATV) voor elk jaar tussen vandaag en peildatum, met pro-rata
 *   op het peildatum-jaar en op het startDate-jaar.
 * - `verval` = wettelijke vervaldatum vóór peildatum (oudejaars verlof) of
 *   TVT-overdracht-limiet bij jaarwisseling.
 *
 * Genereert vijf typen waarschuwingen wanneer relevant.
 */
export function berekenVerwachtSaldo(input: VerwachtSaldoInput): VerwachtSaldoResult {
  const {
    emmers,
    mutaties,
    contractHoursPerWeek,
    atvEnabled,
    atvHoursPerWeek,
    contractEndDate,
    startDate,
    beleid,
    gereserveerdUren,
    peildatum,
    vandaag,
    minRestUren,
    tvtMaxOverdrachtUren,
  } = input

  const vandaagJaar = vandaag.getFullYear()
  const peildatumJaar = peildatum.getFullYear()
  const eersteJaarwisseling = new Date(vandaagJaar, 11, 31)

  // De jaartoekenning van een komend jaar komt één keer per type, niet per emmer-rij.
  // Een medewerker heeft vaak meerdere rijen van hetzelfde type (bovenwettelijk blijft
  // vijf jaar geldig); per rij tellen gaf twee- of driemaal de toekenning. Alleen de
  // nieuwste rij per type draagt de toekomstige toekenning.
  const toekenningsDragerPerType = new Map<SaldoEmmerType, SaldoEmmerInfo>()
  for (const e of emmers) {
    const huidigeDrager = toekenningsDragerPerType.get(e.type)
    if (
      !huidigeDrager ||
      e.jaar > huidigeDrager.jaar ||
      (e.jaar === huidigeDrager.jaar && e.toekenningsDatum > huidigeDrager.toekenningsDatum)
    ) {
      toekenningsDragerPerType.set(e.type, e)
    }
  }

  const perEmmer: EmmerVerwachting[] = emmers.map((emmer) => {
    // 1) Huidig beschikbaar saldo op vandaag.
    const huidigStand = berekenSaldoOpDatum(emmer, mutaties, vandaag)
    const huidig = huidigStand.beschikbaar

    // 2) Toekomstige opbouw binnen huidige emmer (alleen relevant in vandaag-jaar).
    let toekomstigeOpbouw = 0
    if (emmer.type === 'WETTELIJK' || emmer.type === 'BOVENWETTELIJK') {
      const opbouwEinde = peildatumJaar === vandaagJaar ? peildatum : new Date(vandaagJaar, 11, 31)
      const dagenInJaar = isLeapYear(vandaagJaar) ? 366 : 365
      const dagenDelta = getDayOfYear(opbouwEinde) - getDayOfYear(vandaag)
      const opgebouwdNu = berekenActueelOpgebouwd(emmer, vandaag, beleid)
      const ruimte = Math.max(0, emmer.toegekend - opgebouwdNu)
      const verwachtDelta = (dagenDelta / dagenInJaar) * emmer.toegekend
      toekomstigeOpbouw = Math.max(0, Math.min(ruimte, verwachtDelta))
    } else if (emmer.type === 'ATV') {
      const opbouwEinde = peildatumJaar === vandaagJaar ? peildatum : new Date(vandaagJaar, 11, 31)
      const opgebouwdNu = berekenActueelOpgebouwd(emmer, vandaag, beleid)
      const opgebouwdEinde = berekenActueelOpgebouwd(emmer, opbouwEinde, beleid)
      toekomstigeOpbouw = Math.max(0, opgebouwdEinde - opgebouwdNu)
    } else {
      // TVT_WERK / TVT_REIS: geen formule-opbouw.
      toekomstigeOpbouw = 0
    }

    // 3) Toekomstige toekenningen voor jaren > vandaagJaar t/m peildatumJaar, alleen op de
    //    toekenningsdrager van dit type (zie boven).
    let toekomstigeToekenning = 0
    if (peildatumJaar > vandaagJaar && toekenningsDragerPerType.get(emmer.type)?.id === emmer.id) {
      for (let jaar = vandaagJaar + 1; jaar <= peildatumJaar; jaar++) {
        let jaarUren = 0
        if (emmer.type === 'WETTELIJK') {
          jaarUren = berekenWettelijkOpbouw(contractHoursPerWeek, beleid)
        } else if (emmer.type === 'BOVENWETTELIJK') {
          jaarUren = berekenBovenwettelijkOpbouw(contractHoursPerWeek, beleid)
        } else if (emmer.type === 'ATV' && atvEnabled) {
          jaarUren = berekenATVOpbouw(
            contractHoursPerWeek,
            atvHoursPerWeek + contractHoursPerWeek,
            beleid,
          )
        }

        if (jaarUren <= 0) continue

        // Pro-rata voor het peildatum-jaar (alleen tot peildatum, niet hele jaar).
        if (jaar === peildatumJaar) {
          jaarUren = proRataUren(jaarUren, jaar, { start: new Date(jaar, 0, 1), eind: peildatum })
        }

        // Pro-rata wanneer startDate binnen het toekenning-jaar valt.
        if (startDate && startDate.getFullYear() === jaar && startDate > new Date(jaar, 0, 1)) {
          jaarUren = proRataUren(jaarUren, jaar, { start: startDate })
        }

        toekomstigeToekenning += jaarUren
      }
    }

    // 4) Verval.
    let verval = 0
    let vervalDatumOut: Date | null = null
    if (emmer.type === 'WETTELIJK') {
      if (emmer.vervalDatum && emmer.vervalDatum < peildatum) {
        // Wettelijk vervalt: huidig openstaand saldo gaat verloren.
        verval = Math.max(0, huidig)
        vervalDatumOut = emmer.vervalDatum
      }
    } else if (emmer.type === 'TVT_WERK' || emmer.type === 'TVT_REIS') {
      if (peildatum >= eersteJaarwisseling) {
        const maxOverdracht =
          tvtMaxOverdrachtUren ?? beleid.tvtMaxOverdrachtWeken * contractHoursPerWeek
        verval = Math.max(0, huidig - maxOverdracht)
        if (verval > 0) vervalDatumOut = eersteJaarwisseling
      }
    }

    const verwacht = huidig + toekomstigeOpbouw + toekomstigeToekenning - verval

    return {
      type: emmer.type,
      huidig: roundToKwartier(huidig),
      toekomstigeOpbouw: roundToKwartier(toekomstigeOpbouw),
      toekomstigeToekenning: roundToKwartier(toekomstigeToekenning),
      verval: roundToKwartier(verval),
      verwacht: roundToKwartier(verwacht),
      _vervalDatum: vervalDatumOut,
    } as EmmerVerwachting & { _vervalDatum: Date | null }
  })

  // Totalen.
  const huidigBeschikbaar = roundToKwartier(perEmmer.reduce((s, e) => s + e.huidig, 0))
  const verwachtOpPeildatum = roundToKwartier(perEmmer.reduce((s, e) => s + e.verwacht, 0))
  const gereserveerd = roundToKwartier(gereserveerdUren)
  const vrijeRuimte = roundToKwartier(verwachtOpPeildatum - gereserveerd)

  // Waarschuwingen.
  const waarschuwingen: VerwachtSaldoWaarschuwing[] = []
  const drempelRest = minRestUren ?? 16

  if (vrijeRuimte < 0) {
    waarschuwingen.push({
      type: 'ONVOLDOENDE_SALDO',
      details: { tekort: roundToKwartier(-vrijeRuimte) },
    })
  }
  if (vrijeRuimte > 0 && vrijeRuimte < drempelRest) {
    waarschuwingen.push({
      type: 'LAAG_RESTEREND',
      details: { resterend: roundToKwartier(vrijeRuimte) },
    })
  }
  const totaalProjectie = perEmmer.reduce(
    (s, e) => s + e.toekomstigeOpbouw + e.toekomstigeToekenning,
    0,
  )
  if (totaalProjectie > 0) {
    waarschuwingen.push({ type: 'GEBASEERD_OP_VERWACHTE_OPBOUW', details: {} })
  }
  if (contractEndDate && contractEndDate < peildatum) {
    waarschuwingen.push({
      type: 'CONTRACT_EINDIGT_VOOR_VAKANTIE',
      details: { contractEindDatum: contractEndDate },
    })
  }
  const totaalVerval = perEmmer.reduce((s, e) => s + e.verval, 0)
  if (totaalVerval > 0) {
    const eersteVervalDatum = perEmmer
      .filter((e) => e.verval > 0)
      .map((e) => (e as EmmerVerwachting & { _vervalDatum: Date | null })._vervalDatum)
      .filter((dt): dt is Date => dt !== null)
      .sort((a, b) => a.getTime() - b.getTime())[0]
    waarschuwingen.push({
      type: 'VERVAL_VOOR_PEILDATUM',
      details: {
        vervalUren: roundToKwartier(totaalVerval),
        vervalDatum: eersteVervalDatum ?? null,
      },
    })
  }

  // Strip private _vervalDatum-veld uit publieke output.
  const perEmmerOut: EmmerVerwachting[] = perEmmer.map((e) => ({
    type: e.type,
    huidig: e.huidig,
    toekomstigeOpbouw: e.toekomstigeOpbouw,
    toekomstigeToekenning: e.toekomstigeToekenning,
    verval: e.verval,
    verwacht: e.verwacht,
  }))

  return {
    huidigBeschikbaar,
    verwachtOpPeildatum,
    gereserveerd,
    vrijeRuimte,
    perEmmer: perEmmerOut,
    waarschuwingen,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Verlof-procedure-correctie — vervalt-eerst, saldo-marge, kosten, categorie
// (verlof-procedure-correctie.md Deel 3, 5, 7)
// ────────────────────────────────────────────────────────────────────────────

/** Rond een uren-/euro-waarde naar 2 decimalen (presentatie/marge-precisie). */
export function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Float-comparison epsilon zodat exact-gelijk binnen een grens telt. */
const SALDO_EPS = 1e-6

// ────────────────────────────────────────────────────────────────────────────
// batch-69 tvt-saldi (FO-522 §A1) — het afboekbeleid: één bron
// ────────────────────────────────────────────────────────────────────────────
//
// De automatische afboeking (classify-hr, de dry-run, de reservering, de
// herverdeling en elke andere afboekende stap) leest de emmer-lijst en de
// volgorde-modus uit het VerlofBeleid. Valt er niets te lezen (geen beleid, of
// een leeg veld), dan gelden deze defaults. Er bestaat GEEN tweede hardcoded
// lijst meer: de drift-test `afboekbeleid-een-bron-drift.test.ts` faalt op een
// nieuwe kopie in de API.

/** Volgorde-modus van de automatische afboeking (`VerlofBeleid.autoAfboekVolgorde`). */
export type AfboekVolgordeModus = 'VERVALT_EERST' | 'VASTE_LIJST'

export const AFBOEK_VOLGORDE_MODI: readonly AfboekVolgordeModus[] = ['VASTE_LIJST', 'VERVALT_EERST']

/** De emmers die aan de automatische afboeking kunnen meedoen (de klassieke vijf). */
export const AUTO_AFBOEK_EMMER_TYPES = [
  'TVT_WERK',
  'TVT_REIS',
  'ATV',
  'WETTELIJK',
  'BOVENWETTELIJK',
] as const
export type AutoAfboekEmmerType = (typeof AUTO_AFBOEK_EMMER_TYPES)[number]

/**
 * FO-522 §A1 (aanname A1) — standaard: TVT eerst (werk, dan reis), daarna ATV,
 * wettelijk en bovenwettelijk. Per CAO-beleid/BV instelbaar.
 */
export const STANDAARD_AUTO_AFBOEK_EMMERS: readonly AutoAfboekEmmerType[] = [
  'TVT_WERK',
  'TVT_REIS',
  'ATV',
  'WETTELIJK',
  'BOVENWETTELIJK',
]

/** FO-522 §A1 — standaard volgt de afboeking de vaste lijst (TVT altijd eerst). */
export const STANDAARD_AFBOEK_VOLGORDE: AfboekVolgordeModus = 'VASTE_LIJST'

/** Het deel van het beleid dat de automatische afboeking stuurt. */
export interface AfboekBeleid {
  /** Geordend: bij VASTE_LIJST is de positie in deze lijst de afboekvolgorde. */
  autoAfboekEmmers: SaldoEmmerType[]
  autoAfboekVolgorde: AfboekVolgordeModus
}

function isAutoAfboekEmmerType(t: string): t is AutoAfboekEmmerType {
  return (AUTO_AFBOEK_EMMER_TYPES as readonly string[]).includes(t)
}

/**
 * Leest het afboekbeleid uit een (mogelijk ontbrekende) VerlofBeleid-rij:
 * alleen de klassieke vijf emmers tellen mee (ouderschap en senioren hebben hun
 * eigen aanvraagpad), dubbelen vallen weg met behoud van de eerste positie, en
 * een lege of onbekende waarde valt terug op de standaard. Puur.
 */
export function normaliseerAfboekBeleid(
  rij?: {
    autoAfboekEmmers?: readonly string[] | null
    autoAfboekVolgorde?: string | null
  } | null,
): AfboekBeleid {
  const gezien = new Set<AutoAfboekEmmerType>()
  const emmers: AutoAfboekEmmerType[] = []
  for (const t of rij?.autoAfboekEmmers ?? []) {
    if (isAutoAfboekEmmerType(t) && !gezien.has(t)) {
      gezien.add(t)
      emmers.push(t)
    }
  }
  const volgorde = rij?.autoAfboekVolgorde
  return {
    autoAfboekEmmers: emmers.length > 0 ? emmers : [...STANDAARD_AUTO_AFBOEK_EMMERS],
    autoAfboekVolgorde:
      volgorde === 'VERVALT_EERST' || volgorde === 'VASTE_LIJST'
        ? volgorde
        : STANDAARD_AFBOEK_VOLGORDE,
  }
}

/** Vervalt-eerst-vergelijking: vervalDatum ASC (null laatst), dan FIFO op toekenning, dan id. */
function vergelijkVervaltEerst(a: SaldoEmmerInfo, b: SaldoEmmerInfo): number {
  const av = a.vervalDatum
  const bv = b.vervalDatum
  // null vervalDatum (nooit-vervallend) altijd achteraan.
  if (av !== null || bv !== null) {
    if (av === null) return 1
    if (bv === null) return -1
    const delta = av.getTime() - bv.getTime()
    if (delta !== 0) return delta
  }
  // Gelijke (of beide-null) vervalDatum → FIFO op toekenningsDatum, met `id` als
  // deterministische tiebreak zodat de afboekvolgorde reproduceerbaar is (anders
  // is de volgorde bij gelijke datums afhankelijk van DB-volgorde).
  const tk = a.toekenningsDatum.getTime() - b.toekenningsDatum.getTime()
  return tk !== 0 ? tk : a.id.localeCompare(b.id)
}

/**
 * FO-522 §B6 (review N1/N2) - de jaarregel voor afboeken en herverdelen: een
 * emmer van een LATER jaar dan de aanvraag doet nooit mee. Het recht van volgend
 * jaar (dat al vooraf kan zijn toegekend) is geen saldo voor verlof van dit jaar,
 * en verlof uit een afgesloten jaar schuift nooit naar een emmer van een later
 * jaar. Een emmer van een eerder, nog niet vervallen jaar doet wel mee (de oudste
 * eerst, zoals altijd). Zonder opnamejaar (bv. een saldo-overzicht) geen filter.
 */
export function emmerTeltVoorOpnameJaar(
  emmer: { jaar: number },
  opnameJaar: number | null | undefined,
): boolean {
  return opnameJaar == null || emmer.jaar <= opnameJaar
}

/**
 * Bepaalt de automatische afboekvolgorde (procedure-correctie keuze 2, sinds
 * batch-69 met twee modi, FO-522 §A1).
 *
 * - Filtert op `beleid.autoAfboekEmmers` en, met `opties.opnameJaar`, op de
 *   jaarregel (`emmerTeltVoorOpnameJaar`): geen emmers van een later jaar.
 * - `VASTE_LIJST`: de positie van het emmer-type in `autoAfboekEmmers` bepaalt
 *   de volgorde (standaard TVT werk, TVT reis, ATV, wettelijk, bovenwettelijk).
 *   Binnen één type (meerdere jaren) vervalt-eerst: zo gaat overgedragen TVT van
 *   vorig jaar vóór nieuw opgebouwd TVT (§A4).
 * - `VERVALT_EERST`: het gedrag van vóór batch-69 - echte `vervalDatum` ASC (null
 *   laatst), tiebreak `toekenningsDatum` ASC (FIFO) en `id`. De type-volgorde
 *   volgt dan uit de vervaldatums, niet uit de lijst.
 *
 * Geeft een NIEUWE gesorteerde array terug (muteert de input niet).
 */
export function bepaalAfboekVolgorde(
  emmers: SaldoEmmerInfo[],
  beleid: AfboekBeleid,
  opties: { opnameJaar?: number | null } = {},
): SaldoEmmerInfo[] {
  const positie = new Map<SaldoEmmerType, number>()
  beleid.autoAfboekEmmers.forEach((t, i) => {
    if (!positie.has(t)) positie.set(t, i)
  })
  const gefilterd = emmers
    .filter((e) => positie.has(e.type) && emmerTeltVoorOpnameJaar(e, opties.opnameJaar))
    .slice()
  if (beleid.autoAfboekVolgorde === 'VASTE_LIJST') {
    return gefilterd.sort((a, b) => {
      const pa = positie.get(a.type) ?? 0
      const pb = positie.get(b.type) ?? 0
      return pa !== pb ? pa - pb : vergelijkVervaltEerst(a, b)
    })
  }
  return gefilterd.sort(vergelijkVervaltEerst)
}

export interface VoorstelAfboekDeel {
  emmerId: string
  emmerType: SaldoEmmerType
  uren: number
}

/**
 * FO-522 §A2 — uitkomst van een allocatie: de delen die het saldo dekt en het
 * expliciete tekort (uren waarvoor in de volgorde geen saldo is). Het tekort
 * landt NOOIT meer stil op een emmer: de caller beslist (HR-keuze, onbetaald,
 * afwijzen, of bij een projectie zichtbaar laten).
 */
export interface AfboekAllocatie {
  delen: VoorstelAfboekDeel[]
  /** Niet door saldo gedekte uren (>= 0, 2 decimalen). */
  tekort: number
}

/**
 * Greedy allocatie in de volgorde van `bepaalAfboekVolgorde`: verdeelt `hours`
 * over de al gesorteerde emmers, per emmer tot diens beschikbare saldo. Wat het
 * saldo niet dekt komt als `tekort` terug (FO-522 §A2) - tot batch-69 belandde
 * dat restant stil op de laatste emmer van de volgorde. Puur.
 *
 * De naam is historisch ("vervalt-eerst"); de volgorde komt uit het beleid.
 *
 * Leest alleen `id` + `type` van de emmers, zodat ook een client (classify-
 * modal) met de emmer-volgorde uit de dry-run exact dezelfde verdeling maakt.
 */
export function alloceerVervaltEerst(
  sortedEmmers: ReadonlyArray<Pick<SaldoEmmerInfo, 'id' | 'type'>>,
  beschikbaarPerEmmerId: Map<string, number>,
  hours: number,
): AfboekAllocatie {
  const delen: VoorstelAfboekDeel[] = []
  // PO-besluit 2026-06-01: verlofuren zijn exacte 2 decimalen (geen kwartier-
  // afronding). De daguren-kost is bv. 7,6u of 9u; kwartier-afronding (×4/4)
  // zou 7,6 op 7,5 zetten en een 0,1u-restje laten staan, waardoor de afboek-
  // invariant in classify-hr (teBoekenSaldoUren - geboekt > 1e-6) onterecht
  // GEEN_GESCHIKTE_EMMER gooit. Rond daarom op 2 decimalen.
  let resterend = Math.round(hours * 100) / 100
  if (resterend <= 0) return { delen, tekort: 0 }
  for (const e of sortedEmmers) {
    if (resterend <= 0) break
    const beschikbaar = Math.max(0, beschikbaarPerEmmerId.get(e.id) ?? 0)
    const af = Math.round(Math.min(beschikbaar, resterend) * 100) / 100
    if (af > 0) {
      delen.push({ emmerId: e.id, emmerType: e.type, uren: af })
      resterend = Math.round((resterend - af) * 100) / 100
    }
  }
  return { delen, tekort: Math.max(0, resterend) }
}

/**
 * Reserveert openstaande (pending) verlofaanvragen per emmer volgens dezelfde
 * allocatie als de afboeking bij HR-classificatie (classify Modus 3): elke
 * aanvraag wordt in de beleidsvolgorde over de `autoAfboekEmmers` verdeeld. Eén
 * gedeelde bron voor beide saldo-endpoints zodat het saldo-overzicht en het
 * boekscherm een identiek, waarheidsgetrouw saldo tonen.
 *
 * Anders dan de oude methode-telling reserveert dit een VERLOF-aanvraag NIET
 * dubbel op wettelijk + bovenwettelijk: de uren landen op de werkelijke emmer
 * waarop ze geboekt zullen worden (bv. TVT of ATV als die vooraan staan).
 *
 * `pendingUren` is de lijst losse aanvraag-uren (≥ 0). Opeenvolgende aanvragen
 * delen één aflopende beschikbaar-pool zodat ze niet hetzelfde uur claimen.
 *
 * Reserveringstekort (FO-522): een openstaande aanvraag wordt hier niet
 * geboekt - het tekort beslist HR bij de classificatie. Voor deze PROJECTIE blijft
 * het tekort wel zichtbaar: het telt als reservering op de laatste emmer van de
 * volgorde, zodat het beschikbare saldo daar negatief oogt in plaats van dat de
 * aanvraag uit beeld verdwijnt. Dat is een weergavekeuze, geen boeking.
 *
 * Retourneert een map emmerId → gereserveerde uren (emmers zonder reservering
 * ontbreken in de map).
 */
export function reserveerPerEmmer(
  emmers: SaldoEmmerInfo[],
  beschikbaarPerEmmerId: Map<string, number>,
  pendingUren: number[],
  beleid: AfboekBeleid,
): Map<string, number> {
  const reserved = new Map<string, number>()
  const sorted = bepaalAfboekVolgorde(emmers, beleid)
  if (sorted.length === 0) return reserved
  // Aflopende werk-kopie van het beschikbare saldo per emmer (opgebouwd -
  // opgenomen), zodat meerdere openstaande aanvragen niet hetzelfde uur claimen.
  const werk = new Map<string, number>()
  for (const e of sorted) werk.set(e.id, Math.max(0, beschikbaarPerEmmerId.get(e.id) ?? 0))
  const laatste = sorted[sorted.length - 1]
  for (const uren of pendingUren) {
    if (!(uren > 0)) continue
    const { delen, tekort } = alloceerVervaltEerst(sorted, werk, uren)
    const metTekort =
      tekort > 0 ? [...delen, { emmerId: laatste.id, emmerType: laatste.type, uren: tekort }] : delen
    for (const deel of metTekort) {
      reserved.set(
        deel.emmerId,
        Math.round(((reserved.get(deel.emmerId) ?? 0) + deel.uren) * 100) / 100,
      )
      werk.set(deel.emmerId, Math.round(((werk.get(deel.emmerId) ?? 0) - deel.uren) * 100) / 100)
    }
  }
  return reserved
}

export interface HerverdeelAanvraag {
  /** Identificatie van de aanvraag; alleen om de output terug te koppelen. */
  verlofAanvraagId: string
  /**
   * Huidige afboek-verdeling van deze aanvraag (emmerId → uren). Emmers buiten
   * `autoAfboekEmmers` worden genegeerd en blijven bij de caller ongemoeid.
   */
  huidigPerEmmerId: Record<string, number>
  /**
   * Opnamejaar (FO-522 §B6). Gezet = alleen emmers van dat jaar of een eerder jaar
   * doen mee voor deze aanvraag (`emmerTeltVoorOpnameJaar`).
   */
  jaar?: number
}

export interface HerverdeelAanvraagResultaat {
  verlofAanvraagId: string
  /** True als de nieuwe verdeling afwijkt van de huidige (over de autoAfboek-emmers). */
  changed: boolean
  /** De nieuwe verdeling over de autoAfboek-emmers (inclusief een blijvend tekort). */
  nieuwPerEmmer: VoorstelAfboekDeel[]
  /**
   * FO-522 §A2 — uren die de capaciteit niet dekt. Die blijven op de emmer(s)
   * staan waar ze nu staan (geen stille verschuiving naar een andere emmer).
   */
  tekort: number
}

export interface HerverdeelResultaat {
  /** True als de emmer-totalen verschuiven (`nettoPerEmmer` niet leeg). Een pure
   *  her-toewijzing tussen aanvragen zonder emmer-totaalwijziging telt NIET mee. */
  changed: boolean
  perAanvraag: HerverdeelAanvraagResultaat[]
  /**
   * Netto verschuiving per emmer (nieuw − huidig), gesigneerd; alleen niet-nul
   * emmers. Voor de audit/log-samenvatting ("8u van wettelijk terug naar ATV").
   */
  nettoPerEmmer: VoorstelAfboekDeel[]
  /** Som van de per-aanvraag tekorten (uren die op hun huidige emmer blijven). */
  tekortTotaal: number
}

/**
 * Herberekent de verdeling van al-geboekte verlofaanvragen tegen de actuele
 * emmer-capaciteit en geeft per aanvraag de ideale verdeling terug (spec 38,
 * sinds batch-69 in de beleidsvolgorde en dus inclusief TVT, FO-522 §B5).
 *
 * Lost de "bevroren-verdeling"-bug op: nadat een intrekking/correctie een vroeg-
 * vervallende emmer (bv. ATV, 31-12) vrijspeelt, bleven eerder geboekte uren op
 * een laat-vervallende emmer (wettelijk) staan — met risico dat het vroeg-
 * vervallende saldo onbenut vervalt. AFAS/Nmbrs/Nedap behandelen de verdeling als
 * afgeleid en herberekenen 'm; STREEP bevroor 'm. Deze functie levert de
 * herberekening; de caller schrijft de corrigerende mutaties.
 *
 * - Alleen emmers in `beleid.autoAfboekEmmers` doen mee (ouderschap/senioren en
 *   handmatig-gepinde categorieën blijven ongemoeid); uren op andere emmers in
 *   `huidigPerEmmerId` tellen niet mee en horen niet in de output.
 * - `capaciteitPerEmmerId` = bruto bruikbaar saldo per emmer (toegekend + opbouw −
 *   verval − uitbetaling − niet-herverdeelbaar verbruik). De caller berekent dit;
 *   uitbetaald en vervallen TVT zitten er dus nooit in (§B7).
 * - `aanvragenChronologisch` MOET op boek-/classificatievolgorde staan (oudste
 *   eerst) zodat de vroegste aanvraag de voorste emmer als eerste vult, identiek
 *   aan hoe de aanvragen oorspronkelijk geboekt zouden zijn.
 * - De jaarregel (§B6, review N1/L3): een emmer van een later jaar dan de aanvraag
 *   doet niet mee (`emmerTeltVoorOpnameJaar`), voor elk type, net als bij het
 *   afboeken. Verlof uit een afgesloten jaar herverdeelt dus alleen binnen de
 *   emmers van dat jaar of eerder (spec 38-zelfherstel); een tekort daar gaat via
 *   het rapport met handmatig besluit. Een aanvraag voor volgend jaar mag een
 *   emmer van dit jaar houden zolang die niet vervallen is (capaciteit 0 daarna).
 * - Tekort (§A2): dekt de capaciteit een aanvraag niet volledig, dan blijft het
 *   restant staan waar het nu staat - op de emmers van die aanvraag, de minst
 *   voorkeurige eerst. Nooit een stille verschuiving naar de laatste emmer.
 *
 * Idempotent: is de verdeling al optimaal, dan `changed=false` en lege netto.
 */
export function berekenHerverdeling(
  emmers: SaldoEmmerInfo[],
  capaciteitPerEmmerId: Map<string, number>,
  aanvragenChronologisch: HerverdeelAanvraag[],
  beleid: AfboekBeleid,
): HerverdeelResultaat {
  const sorted = bepaalAfboekVolgorde(emmers, beleid)
  const inSet = new Set(sorted.map((e) => e.id))
  const typeById = new Map(sorted.map((e) => [e.id, e.type]))
  const rangById = new Map(sorted.map((e, i) => [e.id, i]))
  // Aflopende werk-kopie van de capaciteit, gedeeld over de aanvragen (net als
  // reserveerPerEmmer) zodat opeenvolgende aanvragen niet hetzelfde uur claimen.
  const werk = new Map<string, number>()
  for (const e of sorted) werk.set(e.id, Math.max(0, capaciteitPerEmmerId.get(e.id) ?? 0))
  const r2 = (n: number) => Math.round(n * 100) / 100

  const perAanvraag: HerverdeelAanvraagResultaat[] = []
  // Netto verschuiving per emmer (nieuw − huidig), opgeteld over alle aanvragen.
  const nettoMap = new Map<string, { type: SaldoEmmerType; delta: number }>()
  let tekortTotaal = 0

  for (const aanvraag of aanvragenChronologisch) {
    // Te herverdelen totaal = som van het verbruik op de autoAfboek-emmers.
    let totaal = 0
    for (const [emmerId, uren] of Object.entries(aanvraag.huidigPerEmmerId)) {
      if (inSet.has(emmerId)) totaal = r2(totaal + uren)
    }
    const beschikbaarVoorAanvraag = sorted.filter((e) => emmerTeltVoorOpnameJaar(e, aanvraag.jaar))
    const { delen, tekort } = alloceerVervaltEerst(beschikbaarVoorAanvraag, werk, totaal)
    const nieuwMap = new Map(delen.map((deel) => [deel.emmerId, deel.uren]))
    // Tekort blijft staan waar het staat: op de emmers waar de aanvraag nu meer
    // heeft dan de nieuwe verdeling, de minst voorkeurige (achteraan) eerst.
    if (tekort > 0) {
      let rest = tekort
      const huidigeEmmers = Object.keys(aanvraag.huidigPerEmmerId)
        .filter((id) => inSet.has(id))
        .sort((a, b) => (rangById.get(b) ?? 0) - (rangById.get(a) ?? 0))
      for (const emmerId of huidigeEmmers) {
        if (rest <= 0) break
        const ruimte = r2((aanvraag.huidigPerEmmerId[emmerId] ?? 0) - (nieuwMap.get(emmerId) ?? 0))
        if (ruimte <= 0) continue
        const blijft = r2(Math.min(ruimte, rest))
        nieuwMap.set(emmerId, r2((nieuwMap.get(emmerId) ?? 0) + blijft))
        rest = r2(rest - blijft)
      }
      tekortTotaal = r2(tekortTotaal + tekort)
    }
    const nieuw: VoorstelAfboekDeel[] = []
    for (const e of sorted) {
      const uren = nieuwMap.get(e.id)
      if (uren != null && uren > 0) nieuw.push({ emmerId: e.id, emmerType: e.type, uren })
    }
    for (const deel of nieuw) werk.set(deel.emmerId, r2((werk.get(deel.emmerId) ?? 0) - deel.uren))
    // Wijziging bepalen + netto bijwerken: vergelijk nieuw vs huidig per emmer.
    const betrokkenIds = new Set<string>(nieuwMap.keys())
    for (const emmerId of Object.keys(aanvraag.huidigPerEmmerId)) {
      if (inSet.has(emmerId)) betrokkenIds.add(emmerId)
    }
    let changed = false
    for (const emmerId of betrokkenIds) {
      const huidig = inSet.has(emmerId) ? (aanvraag.huidigPerEmmerId[emmerId] ?? 0) : 0
      const na = nieuwMap.get(emmerId) ?? 0
      const delta = r2(na - huidig)
      if (Math.abs(delta) > SALDO_EPS) {
        changed = true
        const type = typeById.get(emmerId) as SaldoEmmerType
        const vorige = nettoMap.get(emmerId)?.delta ?? 0
        nettoMap.set(emmerId, { type, delta: r2(vorige + delta) })
      }
    }
    perAanvraag.push({
      verlofAanvraagId: aanvraag.verlofAanvraagId,
      changed,
      nieuwPerEmmer: nieuw,
      tekort,
    })
  }

  const nettoPerEmmer: VoorstelAfboekDeel[] = []
  for (const [emmerId, { type, delta }] of nettoMap) {
    if (Math.abs(delta) > SALDO_EPS) nettoPerEmmer.push({ emmerId, emmerType: type, uren: delta })
  }
  // `changed` = de EMMER-TOTALEN verschuiven (nettoPerEmmer niet leeg). Een pure
  // her-toewijzing tussen aanvragen die de emmer-totalen gelijk laat (bv. alle
  // aanvragen delen één AFNAME-datum → id-tiebreak wisselt wélke aanvraag welke
  // emmer draagt) telt NIET als wijziging: het levert geen verval-winst op en zou
  // alleen zinloze churn + een lege "voorgestelde verschuiving" geven (#38 review).
  return { changed: nettoPerEmmer.length > 0, perAanvraag, nettoPerEmmer, tekortTotaal }
}

/**
 * De emmers die door een herverdeling onder nul komen of verder de min in gaan.
 * `tekort` blijft bij `berekenHerverdeling` op de huidige emmer staan, ook als een
 * oudere aanvraag die ruimte al heeft ingenomen; zo kan een emmer boven zijn saldo
 * uitkomen. `standPerEmmerId` is de echte stand vóór de herverdeling; `uren` in
 * `netto` is gesigneerd (+ = meer afgeboekt, dus de stand daalt).
 */
export function emmersNegatiefNaHerverdeling(
  standPerEmmerId: Map<string, number>,
  netto: VoorstelAfboekDeel[],
): VoorstelAfboekDeel[] {
  return netto.filter((n) => n.uren > SALDO_EPS && (standPerEmmerId.get(n.emmerId) ?? 0) - n.uren < -SALDO_EPS)
}

// ────────────────────────────────────────────────────────────────────────────
// batch-69 tvt-saldi (FO-513 §A2) — rechttrekken van negatieve saldi met TVT
// ────────────────────────────────────────────────────────────────────────────

/** Eén emmer zoals het rechttrek-voorstel hem ziet (ledger-stand, mag negatief zijn). */
export interface RechttrekEmmer {
  emmerId: string
  type: SaldoEmmerType
  jaar: number
  /** Beschikbaar saldo uit de mutatie-ledger (negatief = in de min). */
  beschikbaar: number
  vervalDatum: Date | null
}

/** Eén verplaatsing in het voorstel: `uren` van een TVT-emmer naar een emmer in de min. */
export interface RechttrekStap {
  vanEmmerId: string
  vanType: SaldoEmmerType
  vanJaar: number
  naarEmmerId: string
  naarType: SaldoEmmerType
  naarJaar: number
  uren: number
}

export interface RechttrekVoorstel {
  stappen: RechttrekStap[]
  /** Totaal ingezette TVT-uren. */
  tvtIngezet: number
  /** Wat na het voorstel nog in de min staat (som over de doel-emmers, positief getal). */
  resterendTekort: number
  /** Tekort vóór het voorstel (som van de negatieve doel-emmers, positief getal). */
  tekortVooraf: number
}

/** FO-513 §A2 — bronvolgorde (eerst TVT werk, dan TVT reis). */
export const RECHTTREK_BRON_TYPES: readonly SaldoEmmerType[] = ['TVT_WERK', 'TVT_REIS']
/** FO-513 §A2 — doelvolgorde (eerst ATV, dan wettelijk, dan bovenwettelijk). */
export const RECHTTREK_DOEL_TYPES: readonly SaldoEmmerType[] = ['ATV', 'WETTELIJK', 'BOVENWETTELIJK']

/**
 * FO-513 §A2 — pure voorstelberekening voor "Rechttrekken met TVT": vult de
 * negatieve emmers (ATV, dan wettelijk, dan bovenwettelijk; binnen een type het
 * oudste jaar eerst) aan uit het positieve TVT-saldo (TVT werk, dan TVT reis;
 * binnen een type vervalt-eerst). Nooit meer dan het beschikbare TVT; wat niet
 * gedekt kan worden blijft als `resterendTekort` zichtbaar.
 */
export function berekenRechttrekVoorstel(emmers: RechttrekEmmer[]): RechttrekVoorstel {
  const r2 = (n: number) => Math.round(n * 100) / 100
  const bronPositie = (t: SaldoEmmerType) => RECHTTREK_BRON_TYPES.indexOf(t)
  const doelPositie = (t: SaldoEmmerType) => RECHTTREK_DOEL_TYPES.indexOf(t)
  const bronnen = emmers
    .filter((e) => bronPositie(e.type) >= 0 && e.beschikbaar > SALDO_EPS)
    .map((e) => ({ ...e, rest: r2(e.beschikbaar) }))
    .sort((a, b) => {
      const p = bronPositie(a.type) - bronPositie(b.type)
      if (p !== 0) return p
      const av = a.vervalDatum?.getTime() ?? Number.MAX_SAFE_INTEGER
      const bv = b.vervalDatum?.getTime() ?? Number.MAX_SAFE_INTEGER
      return av !== bv ? av - bv : a.emmerId.localeCompare(b.emmerId)
    })
  const doelen = emmers
    .filter((e) => doelPositie(e.type) >= 0 && e.beschikbaar < -SALDO_EPS)
    .map((e) => ({ ...e, open: r2(-e.beschikbaar) }))
    .sort((a, b) => {
      const p = doelPositie(a.type) - doelPositie(b.type)
      if (p !== 0) return p
      return a.jaar !== b.jaar ? a.jaar - b.jaar : a.emmerId.localeCompare(b.emmerId)
    })
  const tekortVooraf = r2(doelen.reduce((s, d) => s + d.open, 0))
  const stappen: RechttrekStap[] = []
  let tvtIngezet = 0
  for (const doel of doelen) {
    for (const bron of bronnen) {
      if (doel.open <= SALDO_EPS) break
      if (bron.rest <= SALDO_EPS) continue
      const uren = r2(Math.min(bron.rest, doel.open))
      if (uren <= 0) continue
      stappen.push({
        vanEmmerId: bron.emmerId,
        vanType: bron.type,
        vanJaar: bron.jaar,
        naarEmmerId: doel.emmerId,
        naarType: doel.type,
        naarJaar: doel.jaar,
        uren,
      })
      bron.rest = r2(bron.rest - uren)
      doel.open = r2(doel.open - uren)
      tvtIngezet = r2(tvtIngezet + uren)
    }
  }
  const resterendTekort = r2(doelen.reduce((s, d) => s + Math.max(0, d.open), 0))
  return { stappen, tvtIngezet, resterendTekort, tekortVooraf }
}

// ────────────────────────────────────────────────────────────────────────────
// Verlofschuld — over-opname per emmer detecteren (spec 39)
// ────────────────────────────────────────────────────────────────────────────

export interface VerlofschuldKandidaat {
  emmerId: string
  type: SaldoEmmerType
  jaar: number
  /** De over-opname op deze emmer (positief, = `-beschikbaar`), 2 decimalen. */
  schuldUren: number
}

/**
 * Detecteert per emmer een over-opname (negatief beschikbaar saldo) - de
 * "verlofschuld" die spec 39 expliciet maakt en losklikt van de emmers.
 *
 * - Alleen emmers waarvan het type in `opties.emmerTypes` zit (de autoAfboek-
 *   emmers, default WETTELIJK/BOVENWETTELIJK/ATV) doen mee; TVT/ouderschap/
 *   senioren hebben hun eigen afhandeling en blijven buiten beschouwing.
 * - Gebruikt `berekenSaldoOpDatum` (mutatie-gebaseerd = de bron van waarheid),
 *   niet de `usedHours`-cache.
 * - Puur + idempotent: een niet-negatieve emmer levert geen kandidaat.
 */
export function bepaalVerlofschuldKandidaten(
  emmers: SaldoEmmerInfo[],
  mutaties: SaldoMutatieInfo[],
  peildatum: Date,
  opties: { emmerTypes: SaldoEmmerType[] },
): VerlofschuldKandidaat[] {
  const toegestaan = new Set(opties.emmerTypes)
  const result: VerlofschuldKandidaat[] = []
  for (const e of emmers) {
    if (!toegestaan.has(e.type)) continue
    const stand = berekenSaldoOpDatum(e, mutaties, peildatum, 0)
    if (stand.beschikbaar < -SALDO_EPS) {
      result.push({
        emmerId: e.id,
        type: e.type,
        jaar: e.jaar,
        schuldUren: round2(-stand.beschikbaar),
      })
    }
  }
  return result
}

export interface SaldoRuimteEmmer {
  toegekend: number
  // H6 — beschikbaar voor de marge-check rekent met het werkelijk OPGEBOUWDE saldo
  // (opbouw-/CORRECTIE-mutaties meegenomen, bv. feestdag-naar-rato-compensatie of
  // een handmatige correctie), niet met het kale jaarrecht `toegekend`. Optioneel
  // met fallback naar `toegekend` zodat oudere callers blijven werken.
  opgebouwd?: number
  opgenomen: number
  gereserveerd: number
}

export interface SaldoRuimteInput {
  aantalUren: number
  emmers: SaldoRuimteEmmer[] // huidig jaar — alle emmers
  margePct: number // bv 0.15
  betreftVolgendJaar: boolean // valt (deel van) de aanvraag in volgend jaar?
  volgendJaarRecht: number // Σ toegekend van volgend jaar (0 indien n.v.t.) — mag "geleend" worden
  // Extra reservering die NIET aan een emmer is toegerekend (regel 36): de
  // gereserveerde uren van openstaande KEUZE-LOZE aanvragen (type
  // ONGECLASSIFICEERD, methode null) zijn nog niet op een emmer gealloceerd, dus
  // de per-emmer `gereserveerd` vangt ze niet. Tel ze hier op totaal-niveau mee:
  // ze drukken het beschikbare saldo (en daarmee beschikbaarTotaal), zodat een
  // tweede aanvraag die individueel binnen de marge past, maar samen met de al
  // openstaande aanvraag het saldo+marge overschrijdt, alsnog correct strenger
  // wordt beoordeeld. `jaarrecht`/`margeUren` blijven op Σ toegekend (de marge
  // gaat over het totale RECHT, niet over het door reserveringen verlaagde saldo).
  extraGereserveerd?: number
}

export type SaldoRuimteUitkomst = 'OK' | 'WAARSCHUWING' | 'BLOK'

export interface SaldoRuimteResult {
  beschikbaarTotaal: number
  jaarrecht: number
  margeUren: number
  maxToegestaan: number
  uitkomst: SaldoRuimteUitkomst
  tekortUren: number // max(0, aantalUren - beschikbaarTotaal); hoeveel "de min in"
}

/**
 * Saldo-check bij indienen (procedure-correctie Deel 3): 15%-overschrijdingsmarge
 * over het TOTALE jaarrecht (alle emmers samen) + jaargrens-lening.
 *
 * - `beschikbaarHuidig = Σ(opgebouwd - opgenomen - gereserveerd) - extraGereserveerd`
 *   (opgebouwd = opbouw + toegekend - vervallen; valt terug op toegekend als de
 *   caller geen opgebouwd meegeeft — H6).
 *   over de emmers (gereserveerde uren van openstaande aanvragen tellen mee -
 *   regel 36). `extraGereserveerd` vangt reserveringen die nog niet aan een emmer
 *   zijn toegerekend (keuze-loze, ONGECLASSIFICEERDE aanvragen).
 * - Bij een aanvraag (deels) in volgend jaar mag tegen het volgend-jaar-recht
 *   geleend worden: dat wordt opgeteld bij zowel het beschikbare saldo als het
 *   jaarrecht (waarover de marge wordt berekend).
 * - `margeUren = margePct × jaarrecht`; `maxToegestaan = beschikbaarTotaal + margeUren`.
 *   `jaarrecht` blijft op Σ toegekend (+ leenRecht); `extraGereserveerd` verlaagt
 *   het saldo, NIET het recht.
 * - Uitkomst: ≤ beschikbaar → OK; ≤ max → WAARSCHUWING (mag indienen, "de min in");
 *   > max → BLOK (niet via systeem). Vergelijkingen epsilon-safe zodat exact-gelijk
 *   binnen de grens valt.
 */
export function berekenSaldoRuimte(input: SaldoRuimteInput): SaldoRuimteResult {
  const { aantalUren, emmers, margePct, betreftVolgendJaar, volgendJaarRecht } = input
  const leenRecht = betreftVolgendJaar ? volgendJaarRecht : 0
  const extraGereserveerd = Math.max(0, input.extraGereserveerd ?? 0)

  const beschikbaarHuidig =
    emmers.reduce((s, e) => s + ((e.opgebouwd ?? e.toegekend) - e.opgenomen - e.gereserveerd), 0) -
    extraGereserveerd
  const beschikbaarTotaal = beschikbaarHuidig + leenRecht
  const jaarrecht = emmers.reduce((s, e) => s + e.toegekend, 0) + leenRecht
  const margeUren = margePct * jaarrecht
  const maxToegestaan = beschikbaarTotaal + margeUren

  let uitkomst: SaldoRuimteUitkomst
  if (aantalUren <= beschikbaarTotaal + SALDO_EPS) {
    uitkomst = 'OK'
  } else if (aantalUren <= maxToegestaan + SALDO_EPS) {
    uitkomst = 'WAARSCHUWING'
  } else {
    uitkomst = 'BLOK'
  }

  const tekortUren = Math.max(0, round2(aantalUren - beschikbaarTotaal))

  return {
    beschikbaarTotaal: round2(beschikbaarTotaal),
    jaarrecht: round2(jaarrecht),
    margeUren: round2(margeUren),
    maxToegestaan: round2(maxToegestaan),
    uitkomst,
    tekortUren,
  }
}

/**
 * Kosten-indicatie voor de medewerker (procedure-correctie Deel 2/3):
 * "Een verlofdag kost je X uur" / "Een verlofweek kost je Y uur".
 *
 * - dagUren = `berekenDaguren(werkUrenPerWeek, werkdagenPerWeek)` (bv. 7,6u of 9u).
 * - weekUren = werkdagenPerWeek × dagUren (een volledige werkweek kost je je
 *   werkuren per week; geen hardcoded 5-daagse week meer).
 */
export function berekenVerlofKosten(
  werkUrenPerWeek: number,
  werkdagenPerWeek: number,
): { dagUren: number; weekUren: number } {
  const dagUren = berekenDaguren(werkUrenPerWeek, werkdagenPerWeek)
  return {
    dagUren: round2(dagUren),
    weekUren: round2(werkdagenPerWeek * dagUren),
  }
}

/**
 * Dagkost-cap (Deel 3): een verlofdag kost nooit meer dan de dagwaarde.
 * `kostUren = min(aangevraagde uren, daguren)`; `volledigeDag = aangevraagd >= daguren`.
 * Voor meerdaagse aanvragen: pas per dag toe en sommeer (een hele werkdag kost
 * per definitie de daguren, dus de cap is daar een no-op).
 */
export function berekenVerlofkostDag(
  aangevraagdeUren: number,
  daguren: number,
): { kostUren: number; volledigeDag: boolean } {
  return {
    kostUren: round2(Math.min(aangevraagdeUren, daguren)),
    volledigeDag: aangevraagdeUren >= daguren,
  }
}

/**
 * Zet een dagdeel-tijdvak (begin/eind "HH:mm", op 1 dag) om naar verlof-uren,
 * gecapt op de daguren-kosten via `berekenVerlofkostDag`. PO-besluit 2026-06-01:
 * tijdvak = eind - begin (geen pauze-aftrek, geen rooster-knip).
 *
 * - tijdvakUren < daguren  -> dagdeel:  kosten = tijdvakUren (exact, 2 dec).
 * - tijdvakUren >= daguren -> hele dag: kosten = daguren, isHeleDag = true.
 *
 * Gooit bij ongeldige tijd of eindtijd <= begintijd (zelfde dag; nachtdienst
 * over middernacht is bewust buiten scope v1).
 */
export function berekenDagdeelUren(
  startTijd: string,
  eindTijd: string,
  daguren: number,
): { tijdvakUren: number; kosten: number; isHeleDag: boolean } {
  const start = parseHHmm(startTijd)
  const eind = parseHHmm(eindTijd)
  if (start === null || eind === null) {
    throw new Error('Ongeldige tijd (verwacht HH:mm)')
  }
  if (eind <= start) {
    throw new Error('Eindtijd moet na begintijd liggen')
  }
  const tijdvakUren = round2((eind - start) / 60)
  const { kostUren, volledigeDag } = berekenVerlofkostDag(tijdvakUren, daguren)
  return { tijdvakUren, kosten: kostUren, isHeleDag: volledigeDag }
}

/** Parse "HH:mm" naar minuten sinds middernacht; null bij ongeldig. */
function parseHHmm(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((v ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

export type VerlofSpecialeCategorie = 'MEDISCH' | 'BIJZONDER' | 'OUDERSCHAP' | 'ONBETAALD' | 'WAZO'

export interface CategorieAfboekDeel {
  doel: SaldoEmmerType | VerlofSpecialeCategorie
  uren: number
  isSaldoEmmer: boolean
}

export interface CategorieRegelConfig {
  /** Vast betaald medisch deel voor een REGULIER bezoek (tandarts/dokter). Default 2. */
  medischVastUren?: number // default 2
  // #59 — specialist-variant + jaarlijkse cap (instelbaar via VerlofBeleid, niet hardcoded).
  /** Vast betaald medisch deel voor een SPECIALIST-bezoek. Default 4. */
  medischSpecialistUren?: number // default 4
  /** HR-keuze bij classificatie: true = specialist (4u), false/undefined = regulier (2u). */
  medischIsSpecialist?: boolean
  /** Maximaal betaald medisch verlof per kalenderjaar. Default 8. */
  medischMaxUrenPerJaar?: number // default 8
  /** Reeds dit kalenderjaar opgenomen betaalde medisch-uren (excl. deze aanvraag). */
  medischReedsGebruiktDitJaar?: number
}

/**
 * Past de classificatie-regel van een bijzondere categorie toe (procedure-correctie
 * Deel 5). Geeft terug op welke (saldo- of niet-saldo-)emmers de aangevraagde uren
 * landen.
 *
 * - MEDISCH (#59): HR kiest regulier (default 2u) of specialist (default 4u); dat
 *   vaste deel is betaald (niet-saldo "medisch"-emmer), de rest komt uit ATV. Het
 *   betaalde deel is bovendien gecapt op `medischMaxUrenPerJaar` (default 8) minus
 *   wat dit kalenderjaar al betaald is opgenomen; het meerdere gaat óók uit ATV.
 *   Bv. regulier 8u, 0u verbruikt → 2u medisch + 6u ATV; specialist 8u → 4u + 4u;
 *   regulier 2u met nog maar 1u jaarruimte → 1u medisch + 1u ATV. Alle grenzen zijn
 *   instelbaar (VerlofBeleid), niet hardcoded.
 * - BIJZONDER / OUDERSCHAP / ONBETAALD / WAZO: sane default - het volledige bedrag
 *   op de eigen niet-saldo-emmer van de categorie (HR overschrijft in de UI).
 *   De exacte splitsing/uren per CAO-regel voor deze categorieën zijn
 *   bevestigingspunten in verlof.md - hier worden geen percentages verzonnen.
 */
export function pasCategorieRegelToe(
  categorie: VerlofSpecialeCategorie,
  aangevraagdeUren: number,
  config?: CategorieRegelConfig,
): CategorieAfboekDeel[] {
  const uren = Number.isFinite(aangevraagdeUren) ? Math.max(0, aangevraagdeUren) : 0
  if (uren <= 0) return []

  if (categorie === 'MEDISCH') {
    const regulier = Number.isFinite(config?.medischVastUren ?? NaN)
      ? (config?.medischVastUren as number)
      : 2
    const specialist = Number.isFinite(config?.medischSpecialistUren ?? NaN)
      ? (config?.medischSpecialistUren as number)
      : 4
    const vast = config?.medischIsSpecialist ? specialist : regulier
    const maxPerJaar = Number.isFinite(config?.medischMaxUrenPerJaar ?? NaN)
      ? (config?.medischMaxUrenPerJaar as number)
      : 8
    const reeds = Math.max(0, config?.medischReedsGebruiktDitJaar ?? 0)
    const jaarRuimte = round2(Math.max(0, maxPerJaar - reeds))
    // Betaald medisch deel = min(aangevraagd, vast deze keer, resterende jaarruimte).
    const medischDeel = round2(Math.max(0, Math.min(uren, vast, jaarRuimte)))
    const delen: CategorieAfboekDeel[] = []
    if (medischDeel > 0) delen.push({ doel: 'MEDISCH', uren: medischDeel, isSaldoEmmer: false })
    // De rest (en alles boven de jaar-cap) komt uit het eigen saldo. We labelen het
    // hier als ATV (representatief saldo-deel); de verlof-route boekt het werkelijk
    // af. FO-049-P1: die route cascadeert in de afboekvolgorde van het beleid
    // (sinds batch-69 standaard TVT, ATV, wettelijk, bovenwettelijk), slaat een
    // ontbrekende emmer over (`atvDisabled`) en laat wat geen saldo dekt ONBETAALD
    // vallen — medisch verlof wordt dus nooit door een ontbrekende ATV geblokkeerd.
    const rest = round2(uren - medischDeel)
    if (rest > 0) {
      delen.push({ doel: 'ATV', uren: rest, isSaldoEmmer: true })
    }
    return delen
  }

  // BIJZONDER / OUDERSCHAP / ONBETAALD / WAZO — exacte splits = bevestigingspunt
  // (verlof.md); default: volledig op de eigen niet-saldo-emmer van de categorie.
  return [{ doel: categorie, uren: round2(uren), isSaldoEmmer: false }]
}

export type VerlofMethode = 'VERLOF' | 'ATV' | 'TVT' | 'BIJZONDER' | 'OUDERSCHAP' | 'SENIOREN'

// Emmer → mensvriendelijke soort. WETTELIJK/BOVENWETTELIJK zijn allebei "Vakantie"
// (de monteur hoeft het wettelijk/bovenwettelijk-onderscheid niet in een mail te
// zien); TVT_WERK/TVT_REIS vallen allebei onder "TVT".
const EMMER_SOORT_LABELS: Record<SaldoEmmerType, string> = {
  WETTELIJK: 'Vakantie',
  BOVENWETTELIJK: 'Vakantie',
  ATV: 'ATV',
  TVT_WERK: 'TVT',
  TVT_REIS: 'TVT',
  OUDERSCHAP_BETAALD: 'Ouderschapsverlof (betaald)',
  OUDERSCHAP_ONBETAALD: 'Ouderschapsverlof (onbetaald)',
  SENIORENVERLOF: 'Seniorenverlof',
}

// Methode → soort (consistent met de frontend `VERLOF_METHODE_LABELS`). Dient als
// terugval wanneer er (nog) geen emmer-afboeking is, bv. een submit-/afwijs-mail.
const METHODE_SOORT_LABELS: Record<VerlofMethode, string> = {
  VERLOF: 'Verlof',
  ATV: 'ATV',
  TVT: 'TVT',
  BIJZONDER: 'Bijzonder verlof',
  OUDERSCHAP: 'Ouderschapsverlof',
  SENIOREN: 'Seniorenverlof',
}

const CATEGORIE_SOORT_LABELS: Record<VerlofSpecialeCategorie, string> = {
  MEDISCH: 'Medisch verlof',
  BIJZONDER: 'Bijzonder verlof',
  OUDERSCHAP: 'Ouderschapsverlof',
  ONBETAALD: 'Onbetaald verlof',
  WAZO: 'WAZO-verlof',
}

/**
 * Mensvriendelijke verlof-soort voor in mails en UI. Keuze-loos verlof heeft géén
 * `methode` tot HR classificeert (type blijft `ONGECLASSIFICEERD`), dus de soort
 * komt primair uit de emmer(s) waarop is afgeboekt. Toont **nooit** de rauwe enum.
 *
 * Prioriteit:
 *   1. `categorie` (bijzonder/medisch/etc.) → eigen label;
 *   2. afgeboekte `emmerTypes` → "Vakantie" / "ATV" / "TVT" (uniek, samengevoegd
 *      met " + " bij een vervalt-eerst-split over meerdere soorten);
 *   3. `methode` als terugval (submit-/afwijs-mail vóór classificatie);
 *   4. neutraal "Verlof" wanneer niets bekend is.
 */
export function verlofSoortLabel(input: {
  emmerTypes?: SaldoEmmerType[]
  methode?: VerlofMethode | null
  categorie?: VerlofSpecialeCategorie | null
}): string {
  if (input.categorie) return CATEGORIE_SOORT_LABELS[input.categorie]

  const soorten: string[] = []
  for (const t of input.emmerTypes ?? []) {
    const label = EMMER_SOORT_LABELS[t]
    if (label && !soorten.includes(label)) soorten.push(label)
  }
  if (soorten.length > 0) return soorten.join(' + ')

  if (input.methode) return METHODE_SOORT_LABELS[input.methode]
  return 'Verlof'
}

// ────────────────────────────────────────────────────────────────────────────
// batch-70 vakantiecampagne (FO-525 §B6-§B9) — het jaaroverzicht per potje
// ────────────────────────────────────────────────────────────────────────────
//
// Wat een medewerker in de vakantiecampagne (en op Mijn verlof) per potje ziet:
// start / erbij / opgenomen / correcties / uitbetaald / vervallen / eind, LIVE uit
// het ledger (`SaldoMutatie` + de toekenning op de emmer), nooit uit een bevroren
// momentopname of de `Employee`-cache. Elke correctie die later wordt geboekt
// (FO-522 herverdeling, FO-513 verplaatsing, een handmatige correctie met een
// peildatum in het jaar) werkt daardoor vanzelf door.
//
// Het eind wordt op twee onafhankelijke manieren uitgerekend: als som van de
// componenten en als ledgerstand op 31-12 23:59 via `berekenSaldoOpDatum` (de
// rekenkern van `/leave/v2/balances`). Wijken die af, dan staat het verschil in
// `reconciliatieVerschil`: nooit stil weggewerkt (§B6). Typische oorzaken: een
// mutatie die het saldo stil negeert (een correctie met een vervaldatum in het
// verleden) of een afboeking op een potje dat al was vervallen.

/**
 * Statussen van een verlofaanvraag die het saldo NOG gaan aanspreken: ingediend,
 * aanvullende vraag, goedgekeurd door de teamleider. De afboeking (AFNAME) gebeurt
 * bij de HR-classificatie; vanaf GECLASSIFICEERD staat de aanvraag in het ledger
 * en telt hij niet meer als gereserveerd (§B9: anders gingen dezelfde uren twee
 * keer van het saldo af). Eén bron voor het saldo-detail, de saldo-lijst, de
 * marge bij indienen, de wizard-reservering en het jaaroverzicht.
 */
export const VERLOF_NOG_NIET_AFGEBOEKT_STATUSSEN = ['INGEDIEND', 'AANVULLENDE_VRAAG', 'GOEDGEKEURD_TEAMLEIDER'] as const
export type VerlofNogNietAfgeboektStatus = (typeof VERLOF_NOG_NIET_AFGEBOEKT_STATUSSEN)[number]

export function isVerlofNogNietAfgeboekt(status: string): boolean {
  return (VERLOF_NOG_NIET_AFGEBOEKT_STATUSSEN as readonly string[]).includes(status)
}

/** De vijf klassieke potjes in weergavevolgorde (ouderschap en senioren hebben eigen paden). */
export const KLASSIEKE_EMMER_TYPES: readonly AutoAfboekEmmerType[] = [
  'WETTELIJK',
  'BOVENWETTELIJK',
  'ATV',
  'TVT_WERK',
  'TVT_REIS',
]
export type KlassiekeEmmerType = AutoAfboekEmmerType

/** Een ledgerregel zoals het jaaroverzicht hem leest (meer velden dan `SaldoMutatieInfo`). */
export interface JaaroverzichtMutatie extends SaldoMutatieInfo {
  /** `SaldoMutatieBron` als string (puur: geen Prisma-enum). */
  bron: string
  verlofAanvraagId: string | null
  bronReferentie?: string | null
  toelichting?: string | null
  /** userId van wie hem boekte (de API maakt er een naam van). */
  aangemaaktDoor?: string | null
  /** Moment van boeken; met `standPer` telt een later geboekte mutatie niet mee. */
  createdAt?: Date | null
}

/** Een verlofaanvraag: voor de toerekening aan het verlofjaar en de nog geplande opname. */
export interface JaaroverzichtAanvraag {
  id: string
  /** Eerste verlofdag; het verlofjaar is het NL-jaar van deze datum (§B6). */
  startDate: Date
  status: string
  uren: number
  /** Trekt op de klassieke potjes (niet: ouderschap, senioren, bijzonder verlof). */
  klassiek: boolean
  /** Volgorde van de reservering (oudste eerst), zoals `getPendingSaldoUren`. */
  createdAt?: Date | null
}

export interface JaaroverzichtInput {
  jaar: number
  /** Alle niet-verwijderde klassieke emmers van de medewerker, alle jaren. */
  emmers: SaldoEmmerInfo[]
  mutaties: JaaroverzichtMutatie[]
  /** De aanvragen waar mutaties naar verwijzen plus de openstaande aanvragen. */
  aanvragen: JaaroverzichtAanvraag[]
  /** Het afboekbeleid: verdeelt de nog geplande opname over de potjes (zoals `/balances`). */
  beleid: AfboekBeleid
  /**
   * Startdatum dienstverband. Was de medewerker op 1 januari in dienst, dan is de
   * jaartoekenning van wettelijk en bovenwettelijk "start" (die staat er op 1-1 in
   * een keer); anders "erbij". ATV bouwt in de loop van het jaar op: altijd "erbij".
   */
  inDienstSinds?: Date | null
  /** Stand per (default: nu). Mutaties die later zijn geboekt tellen niet mee. */
  standPer?: Date
  /** Maximale TVT-overdracht per potje (contracturen x `tvtMaxOverdrachtWeken`); voor `vervaltBijJaareinde`. */
  tvtMaxOverdrachtUren?: number | null
}

/** Eén correctie in het jaar (de "i"-lijst in de wizard): wanneer, hoeveel, waarom, wie. */
export interface JaaroverzichtCorrectieRegel {
  mutatieId: string
  /** Het moment waarop de correctie in het saldo geldt (de peildatum). */
  datum: Date
  /** Wanneer hij werd geboekt (kan in een later jaar liggen). */
  geboektOp: Date | null
  /** Gesigneerd: + is erbij, - is eraf. */
  uren: number
  bron: string
  toelichting: string | null
  aangemaaktDoor: string | null
}

export interface JaaroverzichtEmmer {
  type: KlassiekeEmmerType
  /** Saldo op 1-1: rest van eerdere jaren, TVT-overdracht, en de jaartoekenning die er op 1-1 staat. */
  start: number
  /** In de loop van het jaar: ATV, TVT-opbouw, pro-rata toekenning, contractwijziging. */
  erbij: number
  /** Afgeboekt verlof, toegerekend aan het verlofjaar van de aanvraag (niet aan de boekdatum). */
  opgenomen: number
  /** Ingediend of goedgekeurd verlof in dit jaar dat nog niet is afgeboekt (§B6). */
  nogGereserveerd: number
  correcties: number
  uitbetaald: number
  /** Verval BINNEN het jaar (bv. wettelijk van vorig jaar op 1 juli). Alleen een getal, geen tekst in de wizard. */
  vervallen: number
  /** start + erbij - opgenomen + correcties - uitbetaald - vervallen. */
  eindGeboekt: number
  /** eindGeboekt - nogGereserveerd: wat er op 31-12 over is, vóór het verval op de jaargrens. */
  eind: number
  /**
   * Wat er op de jaargrens vervalt (ATV helemaal, bovenwettelijk na vijf jaar, TVT boven
   * de maximale overdracht). Null als het voor TVT niet te bepalen is (geen maximum).
   */
  vervaltBijJaareinde: number | null
  correctieRegels: JaaroverzichtCorrectieRegel[]
  /** De ledgerstand op 31-12 23:59 via `berekenSaldoOpDatum` (zonder reservering). */
  referentie: number
  /** eindGeboekt - referentie; 0 = sluitend. Nooit stil weggewerkt. */
  reconciliatieVerschil: number
}

export interface JaaroverzichtTotaal {
  start: number
  erbij: number
  opgenomen: number
  nogGereserveerd: number
  correcties: number
  uitbetaald: number
  vervallen: number
  eindGeboekt: number
  eind: number
}

export interface JaaroverzichtResultaat {
  jaar: number
  standPer: Date
  perEmmer: Record<KlassiekeEmmerType, JaaroverzichtEmmer>
  totaal: JaaroverzichtTotaal
  /** Som van de verschillen per potje (gesigneerd). */
  reconciliatieVerschil: number
  /** Alle potjes sluiten (verschil binnen 0,01 uur). */
  sluitend: boolean
}

/** Afrondingsruimte van de reconciliatie: `berekenSaldoOpDatum` rondt per stand op twee decimalen. */
const RECONCILIATIE_TOLERANTIE = 0.01
const GRENS_VENSTER_MS = 48 * 60 * 60 * 1000

/** 1 januari 00:00 Europe/Amsterdam als instant. */
function jaargrens(jaar: number): Date {
  return amsterdamAllDayStartToUtc(`${jaar}-01-01`)
}

function inGrensVenster(d: Date | null | undefined, grens: Date): boolean {
  return d != null && Math.abs(d.getTime() - grens.getTime()) < GRENS_VENSTER_MS
}

/**
 * De automatische jaarovergang: het verval en de overdracht die de crons rond de
 * jaargrens boeken (TVT op 31-12 rond 23:30, ATV en bovenwettelijk op hun
 * vervaldatum 31-12). Die horen bij de overgang naar het nieuwe jaar, niet bij het
 * eind van het oude: het eind is de stand vóór het verval.
 */
function isJaarovergang(m: JaaroverzichtMutatie, grens: Date): boolean {
  const automatisch =
    (m.richting === 'VERVAL' && m.bron === 'VERVAL_AUTOMATISCH') ||
    (m.richting === 'OVERDRACHT' && m.bron === 'OVERDRACHT_VOLGEND_JAAR')
  return automatisch && inGrensVenster(m.datum, grens)
}

/**
 * Berekent het jaaroverzicht per klassiek potje (spec 225 §B6), puur.
 *
 * - Toerekening: een afboeking of vrijgave van een verlofaanvraag (bron
 *   VERLOFAANVRAAG met `verlofAanvraagId`) telt in het verlofjaar van die aanvraag
 *   (`LeaveRequest.startDate`), niet op de boekdatum: verlof van 28-12 dat op 5-1
 *   wordt geclassificeerd hoort bij het oude jaar (T2). Een herverdeling (FO-522)
 *   verschuift dus "opgenomen" tussen potjes in het verlofjaar (T4).
 * - Een correctie telt op zijn `datum` (= de peildatum die HR koos), ook als hij
 *   later is geboekt (T3).
 * - Het eind is de stand vóór het verval op de jaargrens; `vervaltBijJaareinde`
 *   noemt wat er dan vervalt. Verval binnen het jaar staat in `vervallen`.
 * - Reconciliatie: het eind uit de componenten moet gelijk zijn aan de ledgerstand
 *   op 31-12 23:59 (`berekenSaldoOpDatum`, met dezelfde toerekening). Een verschil
 *   staat in `reconciliatieVerschil` (T20).
 */
export function berekenJaaroverzicht(input: JaaroverzichtInput): JaaroverzichtResultaat {
  const { jaar } = input
  const standPer = input.standPer ?? new Date()
  const B0 = jaargrens(jaar)
  const B1 = jaargrens(jaar + 1)
  const eindMoment = new Date(B1.getTime() - 1)
  const startMoment = new Date(B0.getTime() - 1)
  const vlakVoorStart = new Date(B0.getTime() - 2)

  const emmers = input.emmers.filter((e) => (KLASSIEKE_EMMER_TYPES as readonly string[]).includes(e.type))
  const emmerById = new Map(emmers.map((e) => [e.id, e]))
  const aanvraagById = new Map(input.aanvragen.map((a) => [a.id, a]))
  const mutaties = input.mutaties.filter(
    (m) => m.emmerId != null && emmerById.has(m.emmerId) && (m.createdAt == null || m.createdAt <= standPer),
  )
  const mutatiesPerEmmer = new Map<string, JaaroverzichtMutatie[]>()
  for (const m of mutaties) {
    const lijst = mutatiesPerEmmer.get(m.emmerId as string) ?? []
    lijst.push(m)
    mutatiesPerEmmer.set(m.emmerId as string, lijst)
  }

  /** Het moment waarop een mutatie in het overzicht telt (verlofjaar of boekdatum). */
  const moment = (m: JaaroverzichtMutatie): Date => {
    if (m.bron === 'VERLOFAANVRAAG' && m.verlofAanvraagId) {
      const a = aanvraagById.get(m.verlofAanvraagId)
      if (a) return a.startDate
    }
    return m.datum
  }
  const inJaar = (d: Date): boolean => d.getTime() >= B0.getTime() && d.getTime() < B1.getTime()
  const opDag1InDienst = input.inDienstSinds == null || input.inDienstSinds.getTime() <= B0.getTime()
  /** De toekenning van dit jaar telt als "erbij" (ATV, of een instromer na 1-1). */
  const toekenningIsErbij = (e: SaldoEmmerInfo): boolean => e.type === 'ATV' || !opDag1InDienst
  const toegekendTotEnMet = (e: SaldoEmmerInfo): number => (e.jaar <= jaar ? e.toegekend : 0)

  /**
   * De ledgerstand van een emmer op 31-12 23:59, met dezelfde toerekening en vóór
   * het verval op de jaargrens. `zonderVerval` = alsof de emmer niet vervalt (voor
   * het verval binnen het jaar).
   */
  const standEind = (e: SaldoEmmerInfo, zonderVerval = false): number => {
    const vervalEmmer = zonderVerval || inGrensVenster(e.vervalDatum, B1) ? null : e.vervalDatum
    // Een vervallen emmer: het restant vervalt bij het lezen. De vervaldatum per
    // mutatie (bv. TVT-opbouw tot 31-12) is dan al in dat verval opgenomen.
    const verlopen = vervalEmmer != null && eindMoment.getTime() > vervalEmmer.getTime()
    const muts: SaldoMutatieInfo[] = (mutatiesPerEmmer.get(e.id) ?? [])
      .filter((m) => !isJaarovergang(m, B1))
      .map((m) => ({
        id: m.id,
        emmerId: m.emmerId,
        richting: m.richting,
        uren: m.uren,
        datum: isJaarovergang(m, B0) ? startMoment : moment(m),
        vervalDatum: zonderVerval || verlopen || inGrensVenster(m.vervalDatum, B1) ? null : m.vervalDatum,
      }))
    const s = berekenSaldoOpDatum({ ...e, toegekend: toegekendTotEnMet(e), vervalDatum: vervalEmmer }, muts, eindMoment, 0)
    return s.opgebouwd - s.opgenomen
  }

  /** De stand op 1-1 na de jaarovergang, zonder de toekenning die als "erbij" telt. */
  const standStart = (e: SaldoEmmerInfo): number => {
    const vervalEmmer = inGrensVenster(e.vervalDatum, B0) ? vlakVoorStart : e.vervalDatum
    const verlopen = vervalEmmer != null && startMoment.getTime() > vervalEmmer.getTime()
    const muts: SaldoMutatieInfo[] = (mutatiesPerEmmer.get(e.id) ?? [])
      .filter((m) => !isJaarovergang(m, B1))
      .map((m) => ({
        id: m.id,
        emmerId: m.emmerId,
        richting: m.richting,
        uren: m.uren,
        datum: isJaarovergang(m, B0) ? startMoment : moment(m),
        vervalDatum: verlopen ? null : inGrensVenster(m.vervalDatum, B0) ? vlakVoorStart : m.vervalDatum,
      }))
    const toegekend = e.jaar < jaar ? e.toegekend : e.jaar === jaar && !toekenningIsErbij(e) ? e.toegekend : 0
    const s = berekenSaldoOpDatum({ ...e, toegekend, vervalDatum: vervalEmmer }, muts, startMoment, 0)
    return s.opgebouwd - s.opgenomen
  }

  // Referentie (ledgerstand op 31-12) per emmer; ook de basis van de reservering.
  const referentiePerEmmer = new Map(emmers.map((e) => [e.id, standEind(e)]))

  // De nog geplande opname: aanvragen met hun eerste verlofdag in dit jaar die nog niet
  // zijn afgeboekt, verdeeld zoals `/balances` dat doet (afboekbeleid, oudste eerst).
  const gepland = input.aanvragen
    .filter((a) => a.klassiek && a.uren > 0 && isVerlofNogNietAfgeboekt(a.status) && inJaar(a.startDate))
    .sort((a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0) || a.id.localeCompare(b.id))
  const reserveerbaar = emmers.filter(
    (e) => e.jaar <= jaar && (e.vervalDatum == null || e.vervalDatum.getTime() >= B1.getTime() - GRENS_VENSTER_MS),
  )
  const beschikbaarVoorReservering = new Map(
    reserveerbaar.map((e) => [e.id, Math.max(0, referentiePerEmmer.get(e.id) ?? 0)]),
  )
  const gereserveerdPerEmmer = reserveerPerEmmer(
    reserveerbaar,
    beschikbaarVoorReservering,
    gepland.map((a) => a.uren),
    input.beleid,
  )

  const perEmmer = {} as Record<KlassiekeEmmerType, JaaroverzichtEmmer>
  for (const type of KLASSIEKE_EMMER_TYPES) {
    const vanType = emmers.filter((e) => e.type === type)
    let start = 0
    let erbij = 0
    let opgenomen = 0
    let correcties = 0
    let uitbetaald = 0
    let vervallen = 0
    let referentie = 0
    let nogGereserveerd = 0
    let vervaltBijJaareinde = 0
    const correctieRegels: JaaroverzichtCorrectieRegel[] = []

    for (const e of vanType) {
      start += standStart(e)
      if (e.jaar === jaar && toekenningIsErbij(e)) erbij += e.toegekend
      const ref = referentiePerEmmer.get(e.id) ?? 0
      referentie += ref
      const gereserveerd = gereserveerdPerEmmer.get(e.id) ?? 0
      nogGereserveerd += gereserveerd
      // Verval binnen het jaar (niet op een jaargrens): wat er bij het lezen vervalt.
      if (
        e.vervalDatum != null &&
        inJaar(e.vervalDatum) &&
        !inGrensVenster(e.vervalDatum, B0) &&
        !inGrensVenster(e.vervalDatum, B1)
      ) {
        vervallen += standEind(e, true) - ref
      }
      if (type !== 'TVT_WERK' && type !== 'TVT_REIS' && inGrensVenster(e.vervalDatum, B1)) {
        vervaltBijJaareinde += Math.max(0, ref - gereserveerd)
      }

      for (const m of mutatiesPerEmmer.get(e.id) ?? []) {
        if (isJaarovergang(m, B0) || isJaarovergang(m, B1)) continue
        const t = moment(m)
        if (!inJaar(t)) continue
        const uren = m.uren
        if (m.bron === 'KEUZEBUDGET_INRUIL') {
          // Verlofuren ingeruild voor het keuzebudget (en de terugboeking bij afkeur).
          uitbetaald += m.richting === 'AFNAME' ? Math.abs(uren) : -uren
        } else if (m.richting === 'OPBOUW') {
          erbij += uren
        } else if (m.richting === 'AFNAME') {
          opgenomen += Math.abs(uren)
        } else if (m.richting === 'UITBETALING') {
          uitbetaald += Math.abs(uren)
        } else if (m.richting === 'VERVAL') {
          vervallen += Math.abs(uren)
        } else if (m.richting === 'CORRECTIE' && m.bron === 'VERLOFAANVRAAG') {
          // Vrijgave van een afboeking (intrekken, wijzigen, herverdelen): minder opgenomen.
          opgenomen -= uren
        } else if (m.richting === 'CORRECTIE' && m.bron === 'CONTRACTWIJZIGING') {
          // Pro-rata herrekening van de toekenning na een contractwijziging.
          erbij += uren
        } else {
          correcties += uren
          correctieRegels.push({
            mutatieId: m.id,
            datum: t,
            geboektOp: m.createdAt ?? null,
            uren: round2(uren),
            bron: m.bron,
            toelichting: m.toelichting ?? null,
            aangemaaktDoor: m.aangemaaktDoor ?? null,
          })
        }
      }
    }

    const eindGeboekt = round2(start + erbij - opgenomen + correcties - uitbetaald - vervallen)
    const eind = round2(eindGeboekt - nogGereserveerd)
    const tvt = type === 'TVT_WERK' || type === 'TVT_REIS'
    const verschilRuw = round2(eindGeboekt - round2(referentie))
    perEmmer[type] = {
      type,
      start: round2(start),
      erbij: round2(erbij),
      opgenomen: round2(opgenomen),
      nogGereserveerd: round2(nogGereserveerd),
      correcties: round2(correcties),
      uitbetaald: round2(uitbetaald),
      vervallen: round2(vervallen),
      eindGeboekt,
      eind,
      vervaltBijJaareinde: tvt
        ? input.tvtMaxOverdrachtUren != null
          ? round2(Math.max(0, eind - Math.max(0, input.tvtMaxOverdrachtUren)))
          : null
        : round2(vervaltBijJaareinde),
      correctieRegels: correctieRegels.sort((a, b) => a.datum.getTime() - b.datum.getTime()),
      referentie: round2(referentie),
      reconciliatieVerschil: Math.abs(verschilRuw) <= RECONCILIATIE_TOLERANTIE ? 0 : verschilRuw,
    }
  }

  const som = (veld: keyof JaaroverzichtTotaal): number =>
    round2(KLASSIEKE_EMMER_TYPES.reduce((s, t) => s + perEmmer[t][veld], 0))
  const totaal: JaaroverzichtTotaal = {
    start: som('start'),
    erbij: som('erbij'),
    opgenomen: som('opgenomen'),
    nogGereserveerd: som('nogGereserveerd'),
    correcties: som('correcties'),
    uitbetaald: som('uitbetaald'),
    vervallen: som('vervallen'),
    eindGeboekt: som('eindGeboekt'),
    eind: som('eind'),
  }
  const reconciliatieVerschil = round2(KLASSIEKE_EMMER_TYPES.reduce((s, t) => s + perEmmer[t].reconciliatieVerschil, 0))
  return {
    jaar,
    standPer,
    perEmmer,
    totaal,
    reconciliatieVerschil,
    sluitend: KLASSIEKE_EMMER_TYPES.every((t) => perEmmer[t].reconciliatieVerschil === 0),
  }
}
